import AuthService from '../service/AuthService';
import {
  appendToFilename,
  buildS3Url,
  callLambda,
  formatBytes,
  getOutputNetworks,
  getTimeOrDateInEtTimezone,
  log,
} from '../utilities/functions';
import {
  AUDIENCE_DATA_PATH,
  AUDIENCE_FILE_CA,
  AUDIENCE_FILE_US,
  countryList,
  CLUBS,
  FEED_MAP,
  BACKEND_LAMBDA,
  BACKEND_LAMBDA_ALTERNATIVE,
  SQS_LAMBDA,
  STATS_BUCKET,
  EXPORT_LAMBDA,
  STATS_LAMBDA,
  STATS_LAMBDA_NODE24,
  LGE_ADVERTISER_IDS,
  LGE_AND_REGULATION_ADVERTISER_IDS,
  MVP_BUCKET,
  NETWORKS,
  CLOUDFRONT_PREFIX,
  PLAYLIST_EXPORT,
  ADVERTISERS_EXPORT,
  DASHBOARD_EXPORT,
} from '../utilities/constants';
import { isPlaylistAdvertSelectable } from '../utilities/playlistAdverts';
import { TVR_REGION_OUTPUT_NAMES, isTvrRegionOutput } from '../utilities/tvrRegionOutputs';

const MEDIA_BUCKET = 'test-hub2-media';
const S3_PREFIX = 'test-hub-2/media/event/';
const CENTER_CIRCLE_ZONE_SEQUENCE = 22;

export default class EventService {
  async getCalendarEventData(eventId) {
    const response = await callLambda(
      {
        operation: 'getCalendarEventData',
        data: { eventId },
      },
      BACKEND_LAMBDA
    );

    const [venue, outputs] = response.body || [[], []];
    const outputsNormalized = outputs.map(output => ({
      ...output,
      ded_complete: Boolean(output.ded_complete),
      gv_complete: Boolean(output.gv_complete),
      sv_complete: Boolean(output.sv_complete),
      ice_complete: Boolean(output.ice_complete),
      ccz_complete: Boolean(output.ccz_complete),
    }));
    return { venue, outputs: outputsNormalized };
  }

  // Completeness of expected DED feeds for games in the next 7 ET days,
  // keyed by event id. Games without expected feeds are not included.
  async getCalendarEventCompleteness() {
    const response = await callLambda(
      {
        operation: 'getCalendarEventCompleteness',
        data: {},
      },
      BACKEND_LAMBDA
    );

    const rows = Array.isArray(response?.body) ? response.body : [];
    return Object.fromEntries(
      rows.map(row => [
        String(row.e),
        {
          expected: Number(row.x) || 0,
          completed: Number(row.d) || 0,
          incompleteFeeds: row.m ? String(row.m).split(', ') : [],
        },
      ])
    );
  }

  async getPlaylistExport(event, outputId) {
    console.log('Get playlist export for event', event, 'outputId', outputId);
    try {
      const response = await callLambda(
        {
          operation: 'getPlaylistExport',
          data: { event, outputId },
        },
        PLAYLIST_EXPORT
      );
      return response.body;
    } catch (e) {
      console.error(e);
    }
  }

  async getAdvertiserStatistic({ event, clubId, zoneType, includeLGE, ownShare }) {
    try {
      const response = await callLambda(
        {
          operation: 'getAdvertiserStatistic',
          data: { event, clubId, zoneType, includeLGE, ownShare },
        },
        ADVERTISERS_EXPORT
      );
      return response.body;
    } catch (e) {
      console.error(e);
    }
  }
  async getAdvertiserTotals(event, clubId) {
    try {
      const response = await callLambda(
        {
          operation: 'getAdvertiserTotals',
          data: { event, clubId },
        },
        BACKEND_LAMBDA
      );
      return response.body;
    } catch (e) {
      console.error(e);
    }
  }

  async getDashboardExport(data) {
    try {
      const response = await callLambda(
        {
          operation: 'getDashboardExport',
          data,
        },
        DASHBOARD_EXPORT
      );

      return response?.body ?? response;
    } catch (e) {
      console.error('getDashboardExport failed:', e);
      throw e;
    }
  }

  async getCompData(compId) {
    let queries, comp;
    if (compId) {
      queries = await callLambda({ hub2: true, competition: true, id: compId });
      comp = queries[0][0];
    } else {
      queries = await callLambda({ hub2: true, competition: true });
    }
    const unsortedSports = this.sortObjectsByName(queries[1]);

    const venues = this.handleCountryCodes(this.sortObjectsByName(queries[2]));

    const allEvents = queries[3]; // this competition events and template events
    const allSegments = queries[4];
    const outputs = queries.length > 5 ? queries[5] : [];
    const zones = queries.length > 6 ? queries[6] : [];

    const sports = unsortedSports.map(sport => {
      const sportSegmentsAndTriggers = allSegments.filter(segment => segment.sport_id === sport.id);
      // sort by segments and triggers. Triggers have no duration
      let segments = [],
        triggers = [];
      for (let el of sportSegmentsAndTriggers) {
        if (el.duration) segments.push(el);
        else triggers.push(el);
      }
      return { ...sport, segments, triggers };
    });

    const compEventsSegmentsAndTriggers = allSegments.filter(segment => segment.event_id);
    const eventsAndTemplates = allEvents.map(event => {
      const thisEventSegmentsAndTriggers = compEventsSegmentsAndTriggers.filter(
        segment => segment.event_id === event.id
      );
      return {
        ...event,
        venue: venues.find(venue => venue.id === event.venueId),
        sport: sports.find(sport => sport.id === event.sportId),
        outputs: outputs.filter(output => output.event_id === event.id),
        segments: thisEventSegmentsAndTriggers.filter(segment => segment.duration),
        triggers: thisEventSegmentsAndTriggers.filter(segment => !segment.duration),
        zones: zones.filter(zone => zone.event_id === event.id),
      };
    });
    const events = eventsAndTemplates.filter(event => event.competitionId);
    const templates = eventsAndTemplates.filter(event => !event.competitionId);
    if (compId) return { comp, sports, venues, events, templates };
    else return { sports, venues, events, templates };
  }

  async getTemplates() {
    let queries;
    queries = await callLambda({ hub2: true, templates: true });
    console.log(queries);
    const unsortedSports = this.sortObjectsByName(queries[0]);

    const venues = this.handleCountryCodes(this.sortObjectsByName(queries[1]));

    const allEvents = queries[2];
    const allSegments = queries[3];
    const outputs = queries.length > 4 ? queries[4] : [];
    const zones = queries.length > 5 ? queries[5] : [];

    const sports = unsortedSports.map(sport => {
      const sportSegmentsAndTriggers = allSegments.filter(segment => segment.sport_id === sport.id);
      // sort by segments and triggers. Triggers have no duration
      let segments = [],
        triggers = [];
      for (let el of sportSegmentsAndTriggers) {
        if (el.duration) segments.push(el);
        else triggers.push(el);
      }
      return { ...sport, segments, triggers };
    });

    const compEventsSegmentsAndTriggers = allSegments.filter(segment => segment.event_id);
    const events = allEvents.map(event => {
      const thisEventSegmentsAndTriggers = compEventsSegmentsAndTriggers.filter(
        segment => segment.event_id === event.id
      );
      return {
        ...event,
        venue: venues.find(venue => venue.id === event.venueId),
        sport: sports.find(sport => sport.id === event.sportId),
        outputs: outputs.filter(output => output.event_id === event.id),
        segments: thisEventSegmentsAndTriggers.filter(segment => segment.duration),
        triggers: thisEventSegmentsAndTriggers.filter(segment => !segment.duration),
        zones: zones.filter(zone => zone.event_id === event.id),
      };
    });
    return { sports, venues, events };
  }
  async getDayEventVideoAssetList({ eventId, dateString }) {
    if (dateString) {
      let startTime = performance.now();
      const response = await callLambda(
        {
          query: 'getEventVideos',
          data: { dateString },
        },
        'hub2-db-3'
      );
      const endTime = performance.now();

      // set the same file name is in the ZIP export package
      const getExportedAssetName = (file, id, advertiserName) => {
        const cleanAdvertiserName = advertiserName
          .toLowerCase()
          .replace(/ /g, '_')
          .replace(/[^a-zA-Z0-9]+/g, '-');

        return `${id}_${cleanAdvertiserName}.${file.split('.').pop()}`;
      };

      return response.body.map(asset => ({
        ...asset,
        exportedFile: getExportedAssetName(asset.file, asset.id, asset.advertiser),
      }));
    }
  }
  async getEventVideoAssetList(eventId) {
    const response = await callLambda(
      {
        operation: 'getEventVideoAssetList',
        data: { eventId },
      },
      BACKEND_LAMBDA
    );
    // set the same file name is in the ZIP export package
    const getExportedAssetName = (file, id, advertiserName) => {
      const cleanAdvertiserName = advertiserName
        .toLowerCase()
        .replace(/ /g, '_')
        .replace(/[^a-zA-Z0-9]+/g, '-');

      return `${id}_${cleanAdvertiserName}.${file.split('.').pop()}`;
    };

    return response.body.map(asset => ({
      ...asset,
      exportedFile: getExportedAssetName(asset.file, asset.id, asset.advertiser),
    }));
  }
  async checkVideos(videoList) {
    const TEMP_FFMPEG_LAMBDA = 'hub2-video-validator';
    const response = await callLambda({ files: videoList }, TEMP_FFMPEG_LAMBDA);
    return response.body;
  }
  async getPlaylistWizardData({
    currentComptitionId,
    includeIntlFeedsWithoutNetworks = false,
    includeAllFeeds = false,
    outputName,
  }) {
    // season 2025/26 ID (9) is the fallback
    const competitionId = currentComptitionId || 9;
    const response = await callLambda(
      {
        operation: 'getFeedsForPlaylistWizard',
        data: {
          competitionId,
          includeIntlFeedsWithoutNetworks,
          includeAllFeeds,
          outputName,
        },
      },
      BACKEND_LAMBDA
    );
    console.log(
      'v2',
      response?.body.length,
      'items. Size:',
      JSON.stringify(response?.body)?.length
    );
    return response?.body || [];
  }

  async getCalendarEventsV3(user) {
    let startTime = performance.now();
    const minEventId = 10000;
    const nhlEventIds = [2026000001]; // special events that are hidden from clubs
    const nameMap = {
      'Away RSN': 1,
      'Home RSN': 2,
      'US National': 3,
      'CA National': 4,
      'CA National (FR)': 5,
      'Away RSN (FR)': 6,
      'Home RSN (FR)': 7,
      'NORD FB': 8,
      'DACH BT': 9,
      'NOVA IP': 10,
      CISSET: 11,
      'INTL 1': 12,
      SLOVAK: 13,
      ROW: 14,
    };
    // inversed output name map
    const nameMapReverse = Object.fromEntries(
      Object.entries(nameMap).map(([key, value]) => [value, key])
    );
    const response = await callLambda(
      {
        operation: 'getCalendarEventsV3Data',
        data: { minEventId },
      },
      BACKEND_LAMBDA
    );

    log('Calendar', 'Event payload:', formatBytes(JSON.stringify(response.body).length));

    let [events, outputs] = response.body;

    events = events.map(e => ({
      id: e.i,
      name: e.n,
      et_time: e.t,
      competition_id: e.c,
      ded_ready_count: Number(e.dc) || 0,
    }));

    outputs = outputs.map(o => ({
      id: o.i,
      name: nameMapReverse[o.n] || o.n,
      event_id: o.e,
    }));

    let userClubId;
    if (user && user.club) {
      userClubId = user.club;
    }
    const isClubUser = userClubId !== 1;
    let eventList = [...events];

    if (isClubUser) {
      const userClub = CLUBS.find(club => club.id === userClubId);
      if (userClub) {
        eventList = events.filter(
          event =>
            event.name.includes(userClub.code) &&
            !nhlEventIds.includes(event.id)
        );
      } else {
        eventList = [];
      }
    }

    // index outputs by event_id
    const outputsMap = new Map();
    outputs.forEach(output => {
      const eventOutputs = outputsMap.get(output.event_id) || [];
      eventOutputs.push(output);
      outputsMap.set(output.event_id, eventOutputs);
    });

    eventList.forEach(event => {
      event.outputs = outputsMap.get(event.id) || [];
    });

    const endTime = performance.now();
    log('Calendar', 'Loaded in', Math.trunc(endTime - startTime), 'ms');
    return eventList;
  }

  // Calendar label related functions
  async getCalendarLabels({ clubId, eventId = null }) {
    const response = await callLambda(
      {
        operation: 'getCalendarLabels',
        data: { clubId, eventId },
      },
      BACKEND_LAMBDA
    );

    return response.body || [];
  }

  async getCalendarLabelCompetitionForDate({ date = null, eventId = null }) {
    const response = await callLambda(
      {
        operation: 'getCalendarLabelCompetitionForDate',
        data: { date, eventId },
      },
      BACKEND_LAMBDA
    );

    return response.body;
  }

  async createCalendarLabel(data) {
    const response = await callLambda(
      {
        operation: 'createCalendarLabel',
        data,
      },
      BACKEND_LAMBDA
    );

    return response.body;
  }

  async updateCalendarLabel(data) {
    const response = await callLambda(
      {
        operation: 'updateCalendarLabel',
        data,
      },
      BACKEND_LAMBDA
    );

    return response.body;
  }

  async deleteCalendarLabel({ id, clubId }) {
    const response = await callLambda(
      {
        operation: 'deleteCalendarLabel',
        data: { id, clubId },
      },
      BACKEND_LAMBDA
    );

    return response.body;
  }

  async getEvent(id) {
    const queries = await callLambda({ hub2: true, event: true, id: id });
    const event = queries[0][0]; // event data
    const unsortedSports = this.sortObjectsByName(queries[1]);
    const venues = this.handleCountryCodes(this.sortObjectsByName(queries[2]));
    const outputs = queries[3].map(output => {
      const advertisers = queries[5]
        .filter(ader => ader.output_id == output.id)
        .map(ader => ader.name);
      return { ...output, advertisers: advertisers.sort((a, b) => a.localeCompare(b)) };
    }); // this event outputs
    const allSegments = queries[4];
    const zones = queries[6];

    const sports = unsortedSports.map(sport => {
      const sportSegmentsAndTriggers = allSegments.filter(segment => segment.sport_id === sport.id);
      // sort by segments and triggers. Triggers have no duration
      let segments = [],
        triggers = [];
      for (let el of sportSegmentsAndTriggers) {
        if (el.duration) segments.push(el);
        else triggers.push(el);
      }
      return { ...sport, segments, triggers };
    });

    event.venue = venues.find(venue => venue.id === event.venueId);
    event.sport = sports.find(sport => sport.id === event.sportId);
    event.segments = allSegments.filter(segment => segment.event_id === event.id);
    event.zones = zones;

    return { event, sports, venues, outputs };
  }
  groupBy = (arr, property) => {
    return arr.reduce((acc, cur) => {
      acc[cur[property]] = [...(acc[cur[property]] || []), cur];
      return acc;
    }, {});
  };
  groupIntoSubarrays = (arr, key) => {
    return arr.reduce(function (r, a, i) {
      if (!i || r[r.length - 1][0][key] !== a[key]) {
        return r.concat([[a]]);
      }
      r[r.length - 1].push(a);
      return r;
    }, []);
  };

  getExportData = async id => {
    const response = await callLambda(
      {
        operation: 'getExportData', //'getStatisticsData',
        data: { id },
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );

    return response;
  };

  getDashboardData = async ({
    startDate,
    endDate,
    advertiserIds,
    mediaCategory,
    cursor,
    limit,
    forceRefresh,
    competitionId,
    digitalInventoryScope,
  }) => {
    const response = await callLambda(
      {
        operation: 'getDashboardData',
        data: {
          startDate,
          endDate,
          advertiserIds,
          mediaCategory,
          cursor,
          limit,
          forceRefresh,
          competitionId,
          digitalInventoryScope,
        },
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );
    console.log('response', response, JSON.stringify(response).length);
    return response?.body;
  };

  getDashboardTotalValue = async ({
    startDate,
    endDate,
    advertiserIds,
    mediaCategory,
    competitionId,
    territoryPills,
    groupKeys,
    network,
    inventoryType,
    forceRefresh,
  }) => {
    const response = await callLambda(
      {
        operation: 'getDashboardTotalValue',
        data: {
          startDate,
          endDate,
          advertiserIds,
          mediaCategory,
          competitionId,
          territoryPills,
          groupKeys,
          network,
          inventoryType,
          forceRefresh,
        },
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );

    return response?.body;
  };
  getDashboardTotalValueByMediaCategory = async (data = {}) => {
    const response = await callLambda(
      {
        operation: 'getDashboardTotalValueByMediaCategory',
        data,
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );
    return response?.body;
  };
  getDashboardAdvertiserValueTrend = async (data = {}) => {
    const response = await callLambda(
      {
        operation: 'getDashboardAdvertiserValueTrend',
        data,
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );
    return response?.body;
  };
  getDashboardPreviewImpression = async ({
    outputId,
    sequence,
    segmentSeq,
    advertiserId,
    inventoryType,
  }) => {
    const response = await callLambda(
      {
        operation: 'getDashboardPreviewImpression',
        data: {
          outputId,
          sequence,
          segmentSeq,
          advertiserId,
          inventoryType,
        },
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );
    return response?.body;
  };
  getWhitelistedAdvertisers = async (data = {}) => {
    const response = await callLambda(
      {
        operation: 'getWhitelistedAdvertisers',
        data,
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );

    return response?.body;
  };
  getWhitelistedAdvertisersV2 = async (data = {}) => {
    const response = await callLambda(
      {
        operation: 'getWhitelistedAdvertisersV2',
        data,
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );

    return response?.body;
  };

  getDashboardAdvertiserDetails = async ({
    eventId,
    outputId,
    sequence,
    segmentSeq,
    advertiserId,
    advertId,
    inventoryType,
    mediaCategory,
  }) => {
    console.log(eventId, outputId);
    const response = await callLambda(
      {
        operation: 'getDashboardAdvertiserDetails',
        data: {
          eventId,
          outputId,
          sequence,
          segmentSeq,
          advertiserId,
          advertId,
          inventoryType,
          mediaCategory,
        },
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );
    console.log(response);
    const detailsResponse = response?.body;

    if (Number(detailsResponse?.statusCode) >= 400) {
      let errorPayload = detailsResponse?.body;

      if (typeof errorPayload === 'string') {
        try {
          errorPayload = JSON.parse(errorPayload);
        } catch (e) {
          // Keep the original response text when it is not JSON.
        }
      }

      throw new Error(errorPayload?.message || 'Failed to load advertiser details');
    }

    return detailsResponse;
  };
  getDashboardDigitalAdvertiserDetails = async ({
    advertiserId,
    advertId,
    inventoryType,
    feedType,
    date,
    groupKey,
    mediaCategory,
  }) => {
    const response = await callLambda(
      {
        operation: 'getDashboardDigitalAdvertiserDetails',
        data: {
          advertiserId,
          advertId,
          inventoryType,
          feedType,
          date,
          groupKey,
          mediaCategory,
        },
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );

    return response;
  };
  // Full Inventory Type / Network lists of the selected period for the
  // Media Allocation filters (not only the month on screen).
  getDashboardFilterOptions = async ({ startDate, endDate, competitionId, forceRefresh }) => {
    const response = await callLambda(
      {
        operation: 'getDashboardFilterOptions',
        data: { startDate, endDate, competitionId, forceRefresh },
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );

    const payload = response?.body ?? response;
    return typeof payload === 'string' ? JSON.parse(payload) : payload;
  };
  getDashboardEventDetails = async ({ eventId, outputId, mediaCategory, breakdownRows }) => {
    const response = await callLambda(
      {
        operation: 'getDashboardEventDetails',
        data: {
          eventId,
          outputId,
          mediaCategory,
          breakdownRows,
        },
      },
      BACKEND_LAMBDA_ALTERNATIVE
    );

    return response?.body;
  };
  getOutputsAds = async outputsIds => {
    const response = await callLambda(
      {
        operation: 'getOutputsAds',
        data: { outputsIds },
      },
      BACKEND_LAMBDA
    );
    return response.body;
  };
  async getExport(id, exportType, bucket, path, outputId = null) {
    const customEvent = id >= 10005 && id <= 10010;
    const customExportLambda = 'hub2-custom-export-handler';
    const experimentalExportLambda = 'hub2-export-handler-experimental';
    let lambda = customEvent ? customExportLambda : EXPORT_LAMBDA;
    // special case for In-Ice test
    if ([2024021088, 20012, 20013].includes(id) || id >= 2024021197) {
      console.log('In-Ice enabled');
      lambda = experimentalExportLambda;
    }

    const lambdaResponse = await callLambda(
      {
        hub2: true,
        exportType,
        id,
        bucket,
        path,
        outputId,
      },
      lambda
    );
    return lambdaResponse;
  }
  async sendSqsMessage(data) {
    await callLambda(data, SQS_LAMBDA);
  }
  async copyS3Object(data) {
    const response = await callLambda(
      {
        operation: 'copyS3Object',
        data,
      },
      STATS_LAMBDA
    );
    console.log(response);
  }
  async getActualizedReportData(data) {
    const response = await callLambda(
      {
        operation: 'handleActualizedReport',
        data,
      },
      STATS_LAMBDA_NODE24
    );
    console.log(response);
    return response;
  }
  async getActualizedReportDataV2({ fileName }) {
    const actualizedReportLambda = 'hub2-actualized-reports-handler';
    const actualizedPrefix = 'actualized/';
    const response = await callLambda(
      {
        operation: 'handleActualizedReport',
        key: actualizedPrefix + fileName,
      },
      actualizedReportLambda
    );
    console.log(response);
    return response;
  }

  async createEventFeedsCSV(eventId, bucket, path) {
    const response = await callLambda(
      {
        operation: 'createEventFeedsCSV',
        data: {
          eventId,
          path,
          bucket,
        },
      },
      STATS_LAMBDA
    );
    return response;
  }

  async testQueryLambda() {
    const queries = await callLambda({ hub2: true, testQuery: true });
    return queries;
  }
  async getPlaylist_InIceV2(id, user, adType, loadAssets = true, variant = 'inIceV2') {
    const startTime = performance.now();
    const clubId = user.club || 0;

    const [dataResponse, supportingDataResponse] = await Promise.all([
      callLambda(
        {
          operation: 'getPlaylistV3_data',
          data: {
            id,
            adType,
            clubId,
            customTemplate: false,
            playlistVariant: variant === 'centerCircle' ? 'centerCircle' : 'standard',
            templateMapping: variant === 'centerCircle' ? undefined : 'segment-v2',
          },
        },
        BACKEND_LAMBDA
      ),
      callLambda(
        {
          operation: 'getPlaylistAdAssetsData',
          data: { id, adType, variant, loadAssets: loadAssets, clubId },
        },
        BACKEND_LAMBDA
      ),
    ]);

    const assetResponse = loadAssets
      ? {
          body: supportingDataResponse.body?.assets || [],
        }
      : null;
    const adResponse = {
      body: supportingDataResponse.body?.adverts || [],
    };

    console.log('Loaded in', performance.now() - startTime, 'ms');
    if (loadAssets) console.log('Asset:', formatBytes(JSON.stringify(assetResponse).length));
    console.log('Data:', formatBytes(JSON.stringify(dataResponse).length));
    console.log('Ad:', formatBytes(JSON.stringify(adResponse).length));

    let adverts = adResponse.body.map(item => ({
      id: item.i,
      advertiser_id: item.ai,
      club_id: item.c,
      type: item.t,
      format: item.f,
      zones: item.z,
      name: item.n,
      active: item.ac,
      submitted: item.s,
      feeds: item.fd,
      is_placeholder: Boolean(item.ip),
      approved: item.ap == null ? true : Boolean(item.ap),
    }));

    // TODO: Avoid fetching the path from the database,
    // as it is always constructed using the `nhl/ads/${advert_id}/` format.
    let assetMap = new Map();
    if (loadAssets && assetResponse) {
      const assetList = assetResponse.body.map(({ a, i, p, f, c, z, pl }) => ({
        advert_id: a,
        id: i,
        framerate: f,
        code: c,
        zone: z,
        playout: `${CLOUDFRONT_PREFIX}${p}${pl}`,
      }));
      assetMap = assetList.reduce((map, asset) => {
        if (!map.has(asset.advert_id)) {
          map.set(asset.advert_id, []);
        }
        map.get(asset.advert_id).push(asset);
        return map;
      }, new Map());
    }

    adverts = adverts.filter(a => {
      if (a.type !== adType) return false;
      const zone = String(a.zones || '')
        .trim()
        .toLowerCase();
      return variant === 'centerCircle'
        ? zone === 'center'
        : zone === 'corner' || zone === 'neutral';
    });

    let [
      [output], // data[0][0]
      [event], // data[1][0]
      allSegments, // data[2]
      advertisers, // data[3]
      zones, // data[4]
      impressions, // data[5]
      placements, // data[6]
      outputsData, // data[7]
      templates, // data[8]
      outputStatsArr, // data[9]
      broadcasters, // data[10]
      templateEvents = [], // data[11], FY27 In-Ice on a compatible backend
    ] = dataResponse.body;

    const outputStats = outputStatsArr.length > 0 ? outputStatsArr[0] : null;

    const feedOrder = [
      'Away RSN',
      'Home RSN',
      'US National',
      'CA National',
      'CA National (FR)',
      'Away RSN (FR)',
      'Home RSN (FR)',
      'INTL 1',
      'NORD FB',
      'DACH BT',
      'NOVA IP',
      'CISSET',
      'SLOVAK',
      'ROW',
    ];

    const outputs = outputsData
      .filter(outp => !isTvrRegionOutput(outp.name))
      .sort((a, b) => feedOrder.indexOf(a.name) - feedOrder.indexOf(b.name))
      .map(outp => ({
        ...outp,
        broadcasters: broadcasters.filter(b => b.output_id === outp.id),
      }));

    event.segments = allSegments.filter(segment => segment.event_id === event.id);
    event.zones = zones;

    // temporarily commented out until LGE/Blank validation feature is enabled
    // advertisers = advertisers.map(adv => ({...adv, lge: adv.name.includes(' LGE ') }));
    // advertisers = advertisers.map(adv => ({...adv, lge: LGE_AD_IDS.includes(adv.id) }));
    const advertiserMap = new Map(advertisers.map(adv => [adv.id, adv]));

    const sortedAdverts = adverts
      .map(advert => {
        const base = {
          ...advert,
          advertiser: advertiserMap.get(advert.advertiser_id),
        };

        if (loadAssets) {
          base.assets = assetMap.get(advert.id) || [];
        }

        return base;
      })
      .filter(advert => {
        if (loadAssets) {
          return advert.assets.length > 0 || advert.is_placeholder;
        }
        return true;
      })
      .sort((a, b) => a.advertiser.name.localeCompare(b.advertiser.name));

    // allAdverts array includes declined ads as well
    const allAdverts = [...sortedAdverts];

    const activeAdverts = sortedAdverts;

    // ads to show in the left Ad list table:
    // club-related & feed-related & active ads
    const allocatedAdverts = activeAdverts.filter(ad => isPlaylistAdvertSelectable({
      advert: ad,
      outputName: output.name,
      eventHomeClubId: event.home,
      userClubId: user.club,
      eventCompetitionId: event.competition_id,
    }));

    // The blank ad exists in the database but isn't
    // fetched and processed properly because it's inactive and
    // partly hidden from the user in the UI. Therefore, it's
    // artificially 'injected' below into the ad list for DED and GV
    if (adType === 'ded' || adType === 'gv') {
      const blankAd = {
        name: 'Asset',
        id: null,
        advertiser: { id: 0, name: 'Blank', code: 'BLANK', lge: true },
      };

      if (adType === 'ded') {
        blankAd.id = 1;
        blankAd.format = 'Full Takeover';
        blankAd.zones = '1-5';
      } else if (adType === 'gv') {
        blankAd.id = 2;
      }

      allocatedAdverts.unshift(blankAd);
      activeAdverts.unshift(blankAd);
      allAdverts.unshift(blankAd);
    }

    output.networks = getOutputNetworks(event, output.name);
    output.broadcasters = broadcasters.filter(b => b.output_id === output.id);

    if (outputStats) {
      Object.assign(output, {
        json: outputStats.raw,
        statsActive: outputStats.active || 0,
        reviewer: outputStats.reviewer,
        comment: outputStats.comment,
      });
    } else {
      Object.assign(output, {
        json: '',
        statsActive: '',
        reviewer: '',
        comment: '',
      });
    }

    console.log('Calculated in', performance.now() - startTime, 'ms');

    return {
      event,
      output,
      allocatedAdverts,
      adverts: activeAdverts,
      allAdverts,
      advertisers,
      placements,
      impressions,
      outputs,
      templates: variant !== 'centerCircle' && Number(event.competition_id) === 10 && !templateEvents.length
        ? [] : templates,
      templateEvent: templateEvents[0] || null,
      broadcasters,
    };
  }

  async getPlaylist_CCZ(id, user, loadAssets = true) {
    return this.getPlaylist_InIceV2(id, user, 'ice', loadAssets, 'centerCircle');
  }

  // get advertisers allowed to use in national in-ice playlists
  getAllowedAdvertisers = async ({ clubId = 0 }) => {
    const response = await callLambda(
      {
        operation: 'getAllowedAdvertisers',
        data: { clubId },
      },
      BACKEND_LAMBDA
    );
    return response.body;
  };

  // used in Whitelist component for in-ice playlists
  getClubAdvertisers = async ({ clubId }) => {
    const response = await callLambda(
      {
        operation: 'getActiveAdvertisersForClub',
        data: { clubId },
      },
      BACKEND_LAMBDA
    );
    console.log(response.body);
    return response.body;
  };

  removeAdvertiserFromClubWhitelist = async ({ clubId, advertiserId }) => {
    const response = await callLambda(
      {
        operation: 'removeAdvertiserFromClubWhitelist',
        data: { clubId, advertiserId },
      },
      BACKEND_LAMBDA
    );
    console.log(response);
    return response.body;
  };

  addAdvertiserToClubWhitelist = async ({ clubId, advertiserId }) => {
    const response = await callLambda(
      {
        operation: 'addAdvertiserToClubWhitelist',
        data: { clubId, advertiserId },
      },
      BACKEND_LAMBDA
    );
    console.log(response);
    return response.body;
  };

  // share 3D Preview
  async createSharedPlaylist(id, previewType = 'ded') {
    const response = await callLambda(
      {
        operation: 'createSharedPlaylist',
        data: { id, previewType },
      },
      BACKEND_LAMBDA
    );
    return response.body;
  }
  async createSharedPlaylist_Impressions(sharedObj) {
    console.log(sharedObj);
    const response = await callLambda(
      {
        operation: 'createSharedPlaylist_Impressions',
        data: sharedObj,
      },
      BACKEND_LAMBDA
    );
    console.log(response);
    return response.body;
  }

  async getTVRPlaylist(id, user, adType, tvrType) {
    const clubId = user.club ? user.club : 0;
    const response = await callLambda(
      {
        operation: 'getTVRPlaylist',
        data: { id, adType, clubId, customTemplate: false },
      },
      BACKEND_LAMBDA
    );
    const queries = response.body;

    let output = queries[0][0];
    const event = queries[1][0];
    const allSegments = queries[2];
    let adverts = queries[14];
    const advertisers = queries[4];
    let assetList = queries[5];
    const zones = queries[6];
    const impressions = queries[7];
    const placements = queries[8];
    const outputStats = queries[12].length > 0 ? queries[12][0] : null;
    const broadcasters = queries[13];

    const feedOrder = [
      'Away RSN',
      'Home RSN',
      'US National',
      'CA National',
      'CA National (FR)',
      'Away RSN (FR)',
      'Home RSN (FR)',
      'INTL 1',
      'CISSET',
      'DACH BT',
      'FI-TV',
      'NORD FB',
      'NOR-TV',
      'NOVA IP',
      'SLOVAK',
      'ROW',
      'SWE-TV',
      'Viaplay Stream',
      'DAZN CZ',
      'DAZN DE',
      'DAZN ES',
      'DAZN FR',
      'DAZN SK',
      'DAZN UK',
      'DAZN WW',
      ...TVR_REGION_OUTPUT_NAMES,
      'ITV4 UK',
      '2 Spots',
      '10 Spots',
    ];
    let outputs = queries[9].sort((a, b) => feedOrder.indexOf(a.name) - feedOrder.indexOf(b.name));

    // All-Star Game events
    if (event.id < 10100) {
      const feedOrder = ['ESPN', 'SN', 'TVA', 'NHLN'];
      outputs = queries[9].sort((a, b) => feedOrder.indexOf(a.name) - feedOrder.indexOf(b.name));
    }

    outputs = outputs.map(outp => ({
      ...outp,
      broadcasters: broadcasters.filter(b => b.output_id === outp.id),
    }));

    const templates = queries[10];

    event.segments = allSegments.filter(segment => segment.event_id === event.id);
    event.zones = zones;

    assetList = assetList.map(asset => ({
      ...asset,
      thumbnail: CLOUDFRONT_PREFIX + asset.path + asset.thumbnail,
      playout: CLOUDFRONT_PREFIX + asset.path + asset.playout,
      preview: CLOUDFRONT_PREFIX + asset.path + asset.preview,
    }));

    // tv/radio (TODO: incorporate into SQL query)
    const formats = tvrType === 'tv' ? ['TVC', 'TVG'] : ['RAD'];
    adverts = adverts.filter(ad => formats.includes(ad.format));

    // add advertiser and assets info to each advert
    // and then sort adverts array by advertiser name
    adverts = adverts
      .map(advert => ({
        ...advert,
        advertiser: advertisers.find(advertiser => advertiser.id === advert.advertiser_id),
        assets: assetList.filter(asset => asset.advert_id === advert.id),
      }))
      .sort((a, b) => a.advertiser.name.localeCompare(b.advertiser.name));

    // allAdverts includes declined ads as well
    const allAdverts = [...adverts];

    // ads to show in the left Ad list table (club related and active ads only)
    const allocatedAdverts = adverts.filter(ad => ad.club_id === user.club && ad.active);

    output.networks = getOutputNetworks(event, output.name);
    output.broadcasters = broadcasters.filter(b => b.output_id === output.id);

    if (outputStats) {
      output.json = outputStats.raw;
      output.statsActive = outputStats.active || 0;
      output.reviewer = outputStats.reviewer;
      output.comment = outputStats.comment;
    } else {
      Object.assign(output, {
        json: '',
        statsActive: '',
        reviewer: '',
        comment: '',
      });
    }

    return {
      event,
      output,
      allocatedAdverts,
      adverts,
      allAdverts,
      advertisers,
      placements,
      impressions,
      outputs,
      templates,
      broadcasters,
    };
  }

  // As of 24.07.2025, this function is called from the DED playlist page with loadAssets = false by default,
  // since asset data is only needed for raw metrics generation in getStats(), where loadAssets = true.
  // TODO: Update SV and GV playlist pages to call getPlaylist() with loadAssets = false,
  // and apply the same getStats() logic as used in DED.
  async getPlaylist(id, user, adType, loadAssets = true) {
    const startTime = performance.now();
    const clubId = user.club || 0;

    const [dataResponse, supportingDataResponse] = await Promise.all([
      callLambda(
        {
          operation: 'getPlaylistV3_data',
          data: { id, adType, clubId, customTemplate: false,
            templateMapping: ['sv', 'gv'].includes(adType) ? 'segment-v2' : 'segment-v1' },
        },
        BACKEND_LAMBDA
      ),
      callLambda(
        {
          operation: 'getPlaylistAdAssetsData',
          data: { id, adType, variant: 'standard', loadAssets: loadAssets, clubId },
        },
        BACKEND_LAMBDA
      ),
    ]);

    const assetResponse = loadAssets
      ? {
          body: supportingDataResponse.body?.assets || [],
        }
      : null;
    const adResponse = {
      body: supportingDataResponse.body?.adverts || [],
    };

    console.log('Loaded in', performance.now() - startTime, 'ms');
    if (loadAssets) console.log('Asset:', formatBytes(JSON.stringify(assetResponse).length));
    console.log('Data:', formatBytes(JSON.stringify(dataResponse).length));
    console.log('Ad:', formatBytes(JSON.stringify(adResponse).length));

    console.log(adResponse);
    let adverts = adResponse.body.map(item => ({
      id: item.i,
      advertiser_id: item.ai,
      club_id: item.c,
      type: item.t,
      format: item.f,
      zones: item.z,
      name: item.n,
      active: item.ac,
      submitted: item.s,
      feeds: item.fd,
      is_placeholder: Boolean(item.ip),
      bettingRestricted: Boolean(item.br),
      approved: item.ap == null ? true : Boolean(item.ap),
      // DED only: zones with a video asset, e.g. "1,2,4,5" -> [1, 2, 4, 5]
      videoZones: item.vz ? String(item.vz).split(',').map(Number) : [],
    }));

    // TODO: Avoid fetching the path from the database,
    // as it is always constructed using the `nhl/ads/${advert_id}/` format.
    let assetMap = new Map();
    if (loadAssets && assetResponse) {
      const assetList = assetResponse.body.map(({ a, i, p, f, c, z, pl }) => ({
        advert_id: a,
        id: i,
        framerate: f,
        code: c,
        zone: z,
        playout: `${CLOUDFRONT_PREFIX}${p}${pl}`,
      }));
      assetMap = assetList.reduce((map, asset) => {
        if (!map.has(asset.advert_id)) {
          map.set(asset.advert_id, []);
        }
        map.get(asset.advert_id).push(asset);
        return map;
      }, new Map());
    }

    adverts = adverts.filter(a => a.type === adType);

    let [
      [output], // data[0][0]
      [event], // data[1][0]
      allSegments, // data[2]
      advertisers, // data[3]
      zones, // data[4]
      impressions, // data[5]
      placements, // data[6]
      outputsData, // data[7]
      templates, // data[8]
      outputStatsArr, // data[9]
      broadcasters, // data[10]
      templateEvents = [], // data[11], FY27 templates on a compatible backend
    ] = dataResponse.body;

    const outputStats = outputStatsArr.length > 0 ? outputStatsArr[0] : null;

    const feedOrder = [
      'Away RSN',
      'Home RSN',
      'US National',
      'CA National',
      'CA National (FR)',
      'Away RSN (FR)',
      'Home RSN (FR)',
      'INTL 1',
      'NORD FB',
      'DACH BT',
      'NOVA IP',
      'CISSET',
      'SLOVAK',
      'ROW',
    ];

    const outputs = outputsData
      .filter(outp => !isTvrRegionOutput(outp.name))
      .sort((a, b) => feedOrder.indexOf(a.name) - feedOrder.indexOf(b.name))
      .map(outp => ({
        ...outp,
        broadcasters: broadcasters.filter(b => b.output_id === outp.id),
      }));

    event.segments = allSegments.filter(segment => segment.event_id === event.id);
    event.zones = zones;

    // temporarily commented out until LGE/Blank validation feature is enabled
    const LGE_SET = new Set(LGE_ADVERTISER_IDS);
    const LGE_AND_REGULATION_SET = new Set(LGE_AND_REGULATION_ADVERTISER_IDS);
    advertisers = advertisers.map(adv => {
      if (LGE_SET.has(adv.id)) {
        return { ...adv, segment: 'lge' };
      }
      if (LGE_AND_REGULATION_SET.has(adv.id)) {
        return { ...adv, segment: 'any' };
      }
      return adv; // no section is assigned if ads are only suitable for regulation sections (non-LGEs)
    });

    const advertiserMap = new Map(advertisers.map(adv => [adv.id, adv]));

    const sortedAdverts = adverts
      .map(advert => {
        const base = {
          ...advert,
          advertiser: advertiserMap.get(advert.advertiser_id),
        };

        if (loadAssets) {
          base.assets = assetMap.get(advert.id) || [];
        }

        return base;
      })
      .filter(advert => {
        if (loadAssets) {
          return advert.assets.length > 0 || advert.is_placeholder;
        }
        return true;
      })
      .sort((a, b) => a.advertiser.name.localeCompare(b.advertiser.name));

    // allAdverts array includes declined ads as well
    const allAdverts = [...sortedAdverts];

    const activeAdverts = sortedAdverts; //.filter(ad => approvedAdvertIds.has(ad.id));

    // ads to show in the left Ad list table:
    // club-related & feed-related & active ads
    const allocatedAdverts = activeAdverts.filter(ad => isPlaylistAdvertSelectable({
      advert: ad,
      outputName: output.name,
      eventHomeClubId: event.home,
      userClubId: user.club,
      eventCompetitionId: event.competition_id,
    }));

    // The blank ad exists in the database but isn't
    // fetched and processed properly because it's inactive and
    // partly hidden from the user in the UI. Therefore, it's
    // artificially 'injected' below into the ad list for DED and GV
    if (adType === 'ded' || adType === 'gv') {
      const blankAd = {
        name: 'Asset',
        id: null,
        advertiser: { id: 0, name: 'Blank', code: 'BLANK', lge: true },
      };

      if (adType === 'ded') {
        blankAd.id = 1;
        blankAd.format = 'Full Takeover';
        blankAd.zones = '1-5';
      } else if (adType === 'gv') {
        blankAd.id = 2;
      }

      allocatedAdverts.unshift(blankAd);
      activeAdverts.unshift(blankAd);
      allAdverts.unshift(blankAd);
    }

    output.networks = getOutputNetworks(event, output.name);
    output.broadcasters = broadcasters.filter(b => b.output_id === output.id);

    if (outputStats) {
      Object.assign(output, {
        json: outputStats.raw,
        statsActive: outputStats.active || 0,
        reviewer: outputStats.reviewer,
        comment: outputStats.comment,
      });
    } else {
      Object.assign(output, {
        json: '',
        statsActive: '',
        reviewer: '',
        comment: '',
      });
    }

    console.log('Calculated in', performance.now() - startTime, 'ms');

    return {
      event,
      output,
      allocatedAdverts,
      adverts: activeAdverts,
      allAdverts,
      advertisers,
      placements,
      impressions,
      outputs,
      templates: ['ded', 'sv', 'gv'].includes(adType) && Number(event.competition_id) === 10 && !templateEvents.length
        ? [] : templates,
      templateEvent: templateEvents[0] || null,
      broadcasters,
    };
  }

  async getPlaylistV3(id, user, adType) {
    const startTime = performance.now();
    const clubId = user.club || 0;

    const [dataResponse, assetResponse] = await Promise.all([
      callLambda(
        {
          operation: 'getPlaylistV2_data',
          data: { id, adType, clubId, customTemplate: false },
        },
        BACKEND_LAMBDA
      ),
      callLambda(
        {
          operation: 'getPlaylistV3Assets',
          data: { id, adType },
        },
        BACKEND_LAMBDA
      ),
    ]);

    console.log('Loaded in', performance.now() - startTime, 'ms');
    console.log('Asset V2 response size:', JSON.stringify(assetResponse).length);
    console.log('Data response size:', JSON.stringify(dataResponse).length);

    const ASSET_BUCKET = 'hub2-storage';
    const aResponseParsed = assetResponse.body.map(item => ({
      advert_id: item.a,
      id: item.i,
      path: item.p,
      bucket: ASSET_BUCKET,
      framerate: item.f,
      code: item.c,
      zone: item.z,
      playout: item.pl,
    }));

    const assetList = aResponseParsed.map(asset => ({
      ...asset,
      playout: CLOUDFRONT_PREFIX + asset.path + asset.playout,
    }));

    const [
      [output], // data[0][0]
      [event], // data[1][0]
      allSegments, // data[2]
      adverts, // data[3]
      advertisers, // data[4]
      zones, // data[5]
      impressions, // data[6]
      placements, // data[7]
      outputsData, // data[8]
      templates, // data[9]
      approvals, // data[10]
      outputStatsArr, // data[11]
      broadcasters, // data[12]
    ] = dataResponse.body;

    const outputStats = outputStatsArr.length > 0 ? outputStatsArr[0] : null;

    const feedOrder = [
      'Away RSN',
      'Home RSN',
      'US National',
      'CA National',
      'CA National (FR)',
      'Away RSN (FR)',
      'Home RSN (FR)',
      'INTL 1',
      'NORD FB',
      'DACH BT',
      'NOVA IP',
      'CISSET',
      'SLOVAK',
      'ROW',
    ];

    const outputs = outputsData
      .filter(outp => !isTvrRegionOutput(outp.name))
      .sort((a, b) => feedOrder.indexOf(a.name) - feedOrder.indexOf(b.name))
      .map(outp => ({
        ...outp,
        broadcasters: broadcasters.filter(b => b.output_id === outp.id),
      }));

    event.segments = allSegments.filter(segment => segment.event_id === event.id);
    event.zones = zones;

    const advertiserMap = new Map(advertisers.map(adv => [adv.id, adv]));

    const assetMap = assetList.reduce((map, asset) => {
      if (!map.has(asset.advert_id)) {
        map.set(asset.advert_id, []);
      }
      map.get(asset.advert_id).push(asset);
      return map;
    }, new Map());

    const sortedAdverts = adverts
      .map(advert => ({
        ...advert,
        advertiser: advertiserMap.get(advert.advertiser_id),
        assets: assetMap.get(advert.id) || [],
      }))
      .filter(advert => advert.assets.length > 0)
      .sort((a, b) => a.advertiser.name.localeCompare(b.advertiser.name));

    // allAdverts array includes declined ads as well
    const allAdverts = [...sortedAdverts];

    const approvedAdvertIds = new Set(
      approvals.filter(approval => approval.status === 1).map(approval => approval.advert_id)
    );

    const activeAdverts = sortedAdverts.filter(ad => approvedAdvertIds.has(ad.id));

    // ads to show in the left Ad list table:
    // club-related & feed-related & active ads
    const allocatedAdverts = activeAdverts.filter(ad => {
      let feedSpecific = true;
      if (ad.feeds?.length) {
        // FEED_MAP[output.name] is undefined when loading a template;
        // in this case, output.name is defined by the user and is missing
        // from the list of available feeds, so we should skip the feed
        // check to prevent ads from being cut out.
        if (FEED_MAP[output.name]) {
          feedSpecific = ad.feeds.includes(FEED_MAP[output.name]);
        }
        // Separately, check 'Away Share RSN' option:
        // home team ads with this option disabled
        // must be hidden in the away playlists.
        // Note, event.club === user.club checks whether
        // it's a home team user accessing the away feed.
        if (
          feedSpecific &&
          user.club > 1 &&
          !ad.feeds.includes('S') &&
          output.name.includes('Away RSN') &&
          event.home === user.club
        ) {
          feedSpecific = false;
        }
      }
      return ad.club_id === user.club && feedSpecific && ad.active;
    });

    // The blank ad exists in the database but isn't
    // fetched and processed properly because it's inactive and
    // partly hidden from the user in the UI. Therefore, it's
    // artificially 'injected' below into the ad list for DED and GV
    if (adType === 'ded' || adType === 'gv') {
      const blankAd = {
        name: 'Asset',
        id: null,
        advertiser: { id: 0, name: 'Blank', code: 'BLANK' },
      };

      if (adType === 'ded') {
        blankAd.id = 1;
        blankAd.format = 'Full Takeover';
        blankAd.zones = '1-5';
      } else if (adType === 'gv') {
        blankAd.id = 2;
      }

      allocatedAdverts.unshift(blankAd);
      activeAdverts.unshift(blankAd);
      allAdverts.unshift(blankAd);
    }

    output.networks = getOutputNetworks(event, output.name);
    output.broadcasters = broadcasters.filter(b => b.output_id === output.id);

    if (outputStats) {
      Object.assign(output, {
        json: outputStats.raw,
        statsActive: outputStats.active || 0,
        reviewer: outputStats.reviewer,
        comment: outputStats.comment,
      });
    } else {
      Object.assign(output, {
        json: '',
        statsActive: '',
        reviewer: '',
        comment: '',
      });
    }

    console.log('Calculated in', performance.now() - startTime, 'ms');

    return {
      event,
      output,
      allocatedAdverts,
      adverts: activeAdverts,
      allAdverts,
      advertisers,
      placements,
      impressions,
      outputs,
      templates,
      broadcasters,
    };
  }

  async getPlaylistV2(id, user, adType) {
    const startTime = performance.now();
    const clubId = user.club || 0;

    const [dataResponse, assetResponse] = await Promise.all([
      callLambda(
        {
          operation: 'getPlaylistV2_data',
          data: { id, adType, clubId, customTemplate: false },
        },
        BACKEND_LAMBDA
      ),
      callLambda(
        {
          operation: 'getPlaylistV2_assets',
          data: { id, adType, clubId, customTemplate: false },
        },
        BACKEND_LAMBDA
      ),
    ]);

    console.log('Loaded in', performance.now() - startTime, 'ms');
    console.log('Asset response size:', JSON.stringify(assetResponse).length);
    console.log('Data response size:', JSON.stringify(dataResponse).length);

    const assetList = assetResponse.body[0].map(asset => ({
      ...asset,
      playout: CLOUDFRONT_PREFIX + asset.path + asset.playout,
    }));

    const [
      [output], // data[0][0]
      [event], // data[1][0]
      allSegments, // data[2]
      adverts, // data[3]
      advertisers, // data[4]
      zones, // data[5]
      impressions, // data[6]
      placements, // data[7]
      outputsData, // data[8]
      templates, // data[9]
      approvals, // data[10]
      outputStatsArr, // data[11]
      broadcasters, // data[12]
    ] = dataResponse.body;

    const outputStats = outputStatsArr.length > 0 ? outputStatsArr[0] : null;

    const feedOrder = [
      'Away RSN',
      'Home RSN',
      'US National',
      'CA National',
      'CA National (FR)',
      'Away RSN (FR)',
      'Home RSN (FR)',
      'INTL 1',
      'NORD FB',
      'DACH BT',
      'NOVA IP',
      'CISSET',
      'SLOVAK',
      'ROW',
    ];

    const outputs = outputsData
      .filter(outp => !isTvrRegionOutput(outp.name))
      .sort((a, b) => feedOrder.indexOf(a.name) - feedOrder.indexOf(b.name))
      .map(outp => ({
        ...outp,
        broadcasters: broadcasters.filter(b => b.output_id === outp.id),
      }));

    event.segments = allSegments.filter(segment => segment.event_id === event.id);
    event.zones = zones;

    const advertiserMap = new Map(advertisers.map(adv => [adv.id, adv]));

    const assetMap = assetList.reduce((map, asset) => {
      if (!map.has(asset.advert_id)) {
        map.set(asset.advert_id, []);
      }
      map.get(asset.advert_id).push(asset);
      return map;
    }, new Map());

    const sortedAdverts = adverts
      .map(advert => ({
        ...advert,
        advertiser: advertiserMap.get(advert.advertiser_id),
        assets: assetMap.get(advert.id) || [],
      }))
      .filter(advert => advert.assets.length > 0)
      .sort((a, b) => a.advertiser.name.localeCompare(b.advertiser.name));

    // allAdverts array includes declined ads as well
    const allAdverts = [...sortedAdverts];

    const approvedAdvertIds = new Set(
      approvals.filter(approval => approval.status === 1).map(approval => approval.advert_id)
    );

    const activeAdverts = sortedAdverts.filter(ad => approvedAdvertIds.has(ad.id));

    // ads to show in the left Ad list table:
    // club-related & feed-related & active ads
    const allocatedAdverts = activeAdverts.filter(ad => {
      let feedSpecific = true;
      if (ad.feeds?.length) {
        // FEED_MAP[output.name] is undefined when loading a template;
        // in this case, output.name is defined by the user and is missing
        // from the list of available feeds, so we should skip the feed
        // check to prevent ads from being cut out.
        if (FEED_MAP[output.name]) {
          feedSpecific = ad.feeds.includes(FEED_MAP[output.name]);
        }
        // Separately, check 'Away Share RSN' option:
        // home team ads with this option disabled
        // must be hidden in the away playlists.
        // Note, event.club === user.club checks whether
        // it's a home team user accessing the away feed.
        if (
          feedSpecific &&
          user.club > 1 &&
          !ad.feeds.includes('S') &&
          output.name.includes('Away RSN') &&
          event.home === user.club
        ) {
          feedSpecific = false;
        }
      }
      return ad.club_id === user.club && feedSpecific && ad.active;
    });

    // The blank ad exists in the database but isn't
    // fetched and processed properly because it's inactive and
    // partly hidden from the user in the UI. Therefore, it's
    // artificially 'injected' below into the ad list for DED and GV
    if (adType === 'ded' || adType === 'gv') {
      const blankAd = {
        name: 'Asset',
        id: null,
        advertiser: { id: 0, name: 'Blank', code: 'BLANK' },
      };

      if (adType === 'ded') {
        blankAd.id = 1;
        blankAd.format = 'Full Takeover';
        blankAd.zones = '1-5';
      } else if (adType === 'gv') {
        blankAd.id = 2;
      }

      allocatedAdverts.unshift(blankAd);
      activeAdverts.unshift(blankAd);
      allAdverts.unshift(blankAd);
    }

    output.networks = getOutputNetworks(event, output.name);
    output.broadcasters = broadcasters.filter(b => b.output_id === output.id);

    if (outputStats) {
      Object.assign(output, {
        json: outputStats.raw,
        statsActive: outputStats.active || 0,
        reviewer: outputStats.reviewer,
        comment: outputStats.comment,
      });
    } else {
      Object.assign(output, {
        json: '',
        statsActive: '',
        reviewer: '',
        comment: '',
      });
    }

    console.log('Calculated in', performance.now() - startTime, 'ms');

    return {
      event,
      output,
      allocatedAdverts,
      adverts: activeAdverts,
      allAdverts,
      advertisers,
      placements,
      impressions,
      outputs,
      templates,
      broadcasters,
    };
  }

  async reassignEventNetworks(outputs) {
    const response = await callLambda(
      {
        operation: 'reassignEventNetworks',
        data: { outputs },
      },
      BACKEND_LAMBDA
    );
    return response;
  }

  // create raw metrics in the MVP's S3
  // bucket for valuation calculations
  async sendMetricsToMVP(id) {
    const path = 'new';
    const bucket = MVP_BUCKET;
    const response = await callLambda(
      {
        operation: 'createRawMetrics',
        data: {
          outputId: id,
          properId: id,
          path,
          bucket,
        },
      },
      STATS_LAMBDA_NODE24
    );
    return response;
  }

  // generate full CSV report and send it to MVP's bucket
  async sendFullReportToMVP({ fileName, csv }) {
    const path = 'reports';
    const bucket = MVP_BUCKET;
    const STATS_LAMBDA_2 = 'hub2-main-statistics-handler-node-24';

    const response = await callLambda(
      {
        operation: 'sendFullReportToMVP',
        data: {
          csv,
          fileName,
          bucket,
        },
      },
      STATS_LAMBDA_2
    );
    console.log('response from lambda', response);
    return response;
  }

  async updateStats(statsObject) {
    const response = await callLambda(
      {
        operation: 'updateStatsNew',
        data: statsObject,
      },
      BACKEND_LAMBDA
    );
    return response;
  }

  createStats = async (outputId, raw) => {
    const response = await callLambda(
      {
        operation: 'createStatsOutput',
        data: {
          output_id: outputId,
          raw: raw,
        },
      },
      BACKEND_LAMBDA
    );
    return response;
  };

  // update multiple Stats_Output objects
  completeOutputs = async (outputIds, complete) => {
    console.log(outputIds, complete);
    const response = await callLambda(
      {
        operation: 'updateStatsMultiple',
        data: {
          outputIds,
          complete: complete ? '1' : '0',
        },
      },
      BACKEND_LAMBDA
    );
    return response;
  };

  // currently, this function is only used to create an empty stats file;
  // it's called from the Playlist Manager Tools menu.
  saveStatsToS3 = async (data, path) => {
    const jsonString = JSON.stringify(data, null, 1); // 1 space indentation

    const params = {
      Bucket: STATS_BUCKET,
      Key: path,
      Body: jsonString,
      CacheControl: 'no-cache',
    };
    try {
      const authService = new AuthService();
      const s3 = await authService.getS3();
      await s3.putObject(params).promise();
    } catch (error) {
      console.log(error);
    }
  };

  async getTagsAndCategories(user) {
    const response = await callLambda(
      {
        operation: 'getTagsAndCategories',
        data: { clubId: user.club },
      },
      BACKEND_LAMBDA
    );

    return response.body;
  }

  async getTVRAudienceAndCPMs(ids, type = 'tv') {
    const response = await callLambda(
      {
        operation: 'getTVRAudienceAndCPMs',
        data: { ids, type },
      },
      BACKEND_LAMBDA
    );

    return response.body.reduce((acc, item) => {
      const { event_id, output_id } = item;

      if (!output_id) {
        return acc;
      }

      if (!acc[event_id]) {
        acc[event_id] = [];
      }

      const existingIndex = acc[event_id].findIndex(entry => entry.output_id === output_id);

      if (existingIndex === -1) {
        acc[event_id].push(item);
      } else {
        const existingItem = acc[event_id][existingIndex];
        const mergedItem = { ...existingItem };

        Object.keys(item).forEach(key => {
          if (item[key] !== 0 && item[key] !== null && item[key] !== undefined) {
            mergedItem[key] = item[key];
          }
        });

        acc[event_id][existingIndex] = mergedItem;
      }

      return acc;
    }, {});
  }

  async getTVROutputsFromR2NEvents({ eventIds }) {
    const response = await callLambda(
      {
        operation: 'getTVROutputsFromR2NEvents',
        data: { eventIds },
      },
      BACKEND_LAMBDA
    );
    return response.body;
  }

  async getTVRStats(user, season = '2026-27') {
    try {
      const { club } = user;

      const response = await callLambda(
        {
          operation: 'getTVRStatsBySeason',
          data: {
            clubId: club,
            season,
          },
        },
        BACKEND_LAMBDA
      );

      console.log(`TVR stats response size for ${season}:`, JSON.stringify(response).length);

      if (!response?.body) throw new Error('Invalid response from callLambda');

      const { outputs, events } = response.body;

      const outputsMap = new Map();
      for (const outp of outputs) {
        if (!outputsMap.has(outp.event_id)) {
          outputsMap.set(outp.event_id, []);
        }
        outputsMap.get(outp.event_id).push(outp);
      }

      return events
        .sort((a, b) => new Date(a.et_time) - new Date(b.et_time))
        .map(ev => ({
          ...ev,
          date: getTimeOrDateInEtTimezone(ev.et_time, false),
          outputs: outputsMap.get(ev.id) || [],
        }));
    } catch (error) {
      console.error('Error fetching TVR stats:', error);
      return [];
    }
  }
  async getStatsBySeason(user, season = '2026-27') {
    const [response, audienceResponse] = await Promise.all([
      callLambda(
        {
          operation: 'getStatsBySeason',
          data: {
            clubId: user.club,
            season,
          },
        },
        BACKEND_LAMBDA
      ),
      this.getAudienceData({ season }),
    ]);

    const { outputs, events, secondaryOutputs, futureEvents } = response.body;
    console.log(
      '[Reporting] getStatsBySeason response size',
      formatBytes(JSON.stringify(response.body).length)
    );

    const outputMap = new Map(outputs.map(output => [output.id, output]));
    const validSecondaryOutputs = [];

    secondaryOutputs.forEach(secOutput => {
      const primaryOutput = outputMap.get(secOutput.link);
      const originalSecOutput = outputMap.get(secOutput.id);

      if (primaryOutput) {
        validSecondaryOutputs.push({
          ...secOutput,
          primaryName: primaryOutput.name,
          primaryId: primaryOutput.id,
          active: originalSecOutput ? originalSecOutput.active : null,
        });
      }
    });

    const allOutputs = [
      ...outputs.filter(output => !secondaryOutputs.some(o => o.id === output.id)),
      ...validSecondaryOutputs,
    ];

    const outputEventMap = new Map();
    allOutputs.forEach(output => {
      const eventId = output.event_id;
      if (!outputEventMap.has(eventId)) {
        outputEventMap.set(eventId, []);
      }
      outputEventMap.get(eventId).push(output);
    });

    const audienceMap = new Map();
    audienceResponse.forEach(row => {
      if (!row.event) return;

      const eventId = row.event.id;
      if (!audienceMap.has(eventId)) {
        audienceMap.set(eventId, []);
      }
      audienceMap.get(eventId).push(row);
    });

    const availableEvents = [];
    const INTL_FEEDS = [
      'NORD FB',
      'DACH BT',
      'NOVA IP',
      'CISSET',
      'SLOVAK',
      'ROW',
      'FI-TV',
      'NOR-TV',
      'SWE-TV',
      'Viaplay Stream',
      '2 Spots',
      '10 Spots',
      'DAZN CZ',
      'DAZN DE',
      'DAZN ES',
      'DAZN FR',
      'DAZN SK',
      'DAZN UK',
      'DAZN WW',
      'ITV4 UK',
      'FIN',
      'GRMY',
      'SVK',
      'SWE',
      'SWLD',
      'UK',
    ];
    events.forEach(event => {
      const eventOutputs = outputEventMap.get(event.id) || [];
      const eventAudience = audienceMap.get(event.id) || [];

      if (eventOutputs.length > 0) {
        const outputsWithNetworks = eventOutputs.map(output => {
          let audience = 0;
          let complete = false;
          let outputNetworkNames = [];
          let cpm;
          let multipleNetworks = false;
          const intlOutput = INTL_FEEDS.includes(output.name);

          if (output.network) {
            outputNetworkNames = output.network.split(',');

            if (outputNetworkNames.length > 1) {
              multipleNetworks = true;

              const eventNetworkNames = [
                ...(event.national_networks ? event.national_networks.split(',') : []),
                ...(event.away_networks ? event.away_networks.split(',') : []),
                ...(event.home_networks ? event.home_networks.split(',') : []),
              ].filter(Boolean);

              const normalizeNetworkName = value => (value || '').trim().toUpperCase();

              const normalizeNetworksArray = value => {
                if (Array.isArray(value)) {
                  return value
                    .flatMap(item => String(item).split(';'))
                    .map(item => normalizeNetworkName(item))
                    .filter(Boolean)
                    .sort();
                }

                return String(value || '')
                  .split(';')
                  .map(item => normalizeNetworkName(item))
                  .filter(Boolean)
                  .sort();
              };

              outputNetworkNames.sort((a, b) => {
                return eventNetworkNames.indexOf(a) - eventNetworkNames.indexOf(b);
              });

              const audienceValuesForNetworks = intlOutput
                ? outputNetworkNames.map(network =>
                    eventAudience.find(row => {
                      const normalizedNetwork = normalizeNetworkName(network);
                      const normalizedRowNetworks = normalizeNetworksArray(row.networks);
                      const normalizedOutputNetworks = normalizeNetworksArray(outputNetworkNames);

                      const sameFullSet =
                        normalizedRowNetworks.length === normalizedOutputNetworks.length &&
                        normalizedRowNetworks.every(
                          (item, index) => item === normalizedOutputNetworks[index]
                        );

                      if (sameFullSet) return true;

                      return normalizedRowNetworks.includes(normalizedNetwork);
                    })
                  )
                : outputNetworkNames.map(network =>
                    eventAudience.find(row => row.networks === network)
                  );

              audienceValuesForNetworks.sort((a, b) => {
                const aTotal = a?.total ?? -1;
                const bTotal = b?.total ?? -1;
                return bTotal - aTotal;
              });

              if (eventAudience.length > 0) {
                if (!audienceValuesForNetworks[0]) {
                  audience = 0;
                  complete = false;
                } else {
                  audience = intlOutput
                    ? audienceValuesForNetworks[0].intl_est
                    : audienceValuesForNetworks[0].total;
                  complete = audienceValuesForNetworks[0].complete;
                  // spotRate property might be just in one of multiple
                  // audienceValuesForNetworks elements => need to loop through all
                  const networkWithSpotRate = audienceValuesForNetworks.find(
                    item => item && item.spotRate !== undefined
                  );
                  if (networkWithSpotRate) cpm = networkWithSpotRate.spotRate;
                }
              }
            } else {
              const outputAudience = eventAudience.find(
                row => row.networks === outputNetworkNames[0]
              );
              if (outputAudience) {
                audience = intlOutput ? outputAudience.intl_est : outputAudience.total;
                complete = outputAudience.complete;
                cpm = outputAudience.spotRate;
              }
            }
            // solution valid before 03.06.23 (summing up audience values)
            // for (let outputNetworkName of output.network.split(',')) {
            //     const outputAudience = eventAudience
            //         .find(row => row.networks === outputNetworkName);
            //     // when we have multiple networks per feed,
            //     // sum up the audience values for each feed
            //     if (outputAudience) {
            //         audience += outputAudience.total;
            //     }
            // }
          }

          // a quick fix for same-name network event edge case
          // TODO: include outputId === output.id comparison
          // when identifying the correct audience entry for a feed
          if (output.id === 6930) {
            const audienceObj = eventAudience.find(el => el.outputId === output.id);
            if (audienceObj) audience = audienceObj.total;
          }

          if (cpm && cpm.length > 3)
            cpm = cpm[3]; // 4th element is the NHL CPM value
          else cpm = undefined;

          return {
            ...output,
            audience,
            complete,
            cpm,
            ...(multipleNetworks && { network: outputNetworkNames.join(',') }),
          };
        });

        const clubAway = CLUBS.find(club => club.id === event.away);
        const clubHome = CLUBS.find(club => club.id === event.home);
        const away = clubAway ? clubAway.code : '';
        const home = clubHome ? clubHome.code : '';

        const eventDate = getTimeOrDateInEtTimezone(event.et_time, false);
        const eventTime = getTimeOrDateInEtTimezone(event.et_time);

        let name = `${away} @ ${home}`;
        // All-Star special case. TODO: use competition_id instead
        // [TMP]
        const specialEvents = event.id < 10100;

        if (specialEvents) {
          // TODO: fetch event.name from the DB and
          // set name = event.name;
          const allStarMap = {
            10002: 'All-Star 1',
            10003: 'All-Star 2',
            10004: 'All-Star Final',
            10005: 'All-Star Final',
            10006: "Women's Game",
            10007: 'All-Star Skills',
            10009: 'All-Star 1',
            10010: 'All-Star 2',
            10011: 'CA vs SWE',
            10012: 'USA vs FIN',
            10013: 'SWE vs FIN',
            10014: 'USA vs CA',
            10015: 'CA vs FIN',
            10016: 'USA vs SWE',
            10017: 'Final Game',
          };
          name = allStarMap[event.id] || name;
        }

        availableEvents.push({
          ...event,
          name,
          date: eventDate,
          time: eventTime,
          outputs: outputsWithNetworks,
        });
      }
    });

    const getEventName = event => {
      const clubAway = CLUBS.find(club => club.id === event.away);
      const clubHome = CLUBS.find(club => club.id === event.home);
      const away = clubAway ? clubAway.code : '';
      const home = clubHome ? clubHome.code : '';
      return away && home ? `${away} @ ${home}` : '';
    };

    // club users see only the past events
    if (user.club !== 1) {
      return { events: availableEvents };
    }
    return {
      events: [
        ...availableEvents,
        ...(season === '2025-26' || season === '2026-27'
          ? futureEvents.map(ev => ({
              ...ev,
              inFuture: true,
              name: getEventName(ev),
              date: getTimeOrDateInEtTimezone(ev.et_time, false),
              time: getTimeOrDateInEtTimezone(ev.et_time),
            }))
          : []),
      ],
    };
  }

  async deleteAudienceEntries({ ids, isRadio = false }) {
    const response = await callLambda(
      {
        operation: 'deleteAudienceEntries',
        data: { ids, isRadio },
      },
      BACKEND_LAMBDA
    );
    console.log(response);
    return response;
  }

  async getLatestAncillaryAudienceData({ competitionId = 10 } = {}) {
    const response = await callLambda(
      {
        operation: 'getLatestAncillaryAudienceImport',
        data: { competitionId },
      },
      BACKEND_LAMBDA
    );
    const payload = response.body || {};
    const importMetadata = payload.import || null;
    const values = Array.isArray(payload.values) ? payload.values : [];

    if (!importMetadata) {
      return { importMetadata: null, columns: [], rows: [] };
    }

    const persistedTimeframes = Array.isArray(payload.timeframes) ? payload.timeframes : [];
    const timeframes = (persistedTimeframes.length
      ? persistedTimeframes.map(value => [
          Number(value.timeframe_order),
          String(value.timeframe || ''),
        ])
      : [
          ...new Map(
            values.map(value => [Number(value.timeframe_order), String(value.timeframe || '')])
          ).entries(),
        ]
    )
      .sort(([leftOrder], [rightOrder]) => leftOrder - rightOrder)
      .map(([order, header]) => ({ order, field: `period_${order}`, header }));
    const rowsByOrder = new Map();

    values.forEach(value => {
      const networkOrder = Number(value.network_order);
      const row = rowsByOrder.get(networkOrder) || {
        id: `import-${importMetadata.id}-${networkOrder}`,
        network: value.network,
      };
      const numericValue = value.audience_value;
      row[`period_${Number(value.timeframe_order)}`] =
        numericValue !== null && numericValue !== undefined
          ? Number(numericValue)
          : value.raw_value || '';
      rowsByOrder.set(networkOrder, row);
    });

    return {
      importMetadata,
      columns: timeframes.map(({ field, header }) => ({ field, header })),
      rows: [...rowsByOrder.entries()]
        .sort(([leftOrder], [rightOrder]) => leftOrder - rightOrder)
        .map(([, row]) => row),
    };
  }

  async saveAncillaryAudienceData({
    clubId = 1,
    competitionId = 10,
    filename,
    uploadedBy,
    columns,
    rows,
  }) {
    const response = await callLambda(
      {
        operation: 'saveAncillaryAudienceImport',
        data: {
          clubId,
          competitionId,
          filename,
          uploadedBy,
          timeframes: columns.map(column => column.header),
          rows: rows.map(row => ({
            network: row.network,
            values: columns.map(column => row[column.field]),
          })),
        },
      },
      BACKEND_LAMBDA
    );

    return response.body;
  }

  async deleteAncillaryAudienceNetworks({ competitionId = 10, networks, deletedBy }) {
    const response = await callLambda(
      {
        operation: 'deleteAncillaryAudienceNetworks',
        data: { competitionId, networks, deletedBy },
      },
      BACKEND_LAMBDA
    );

    return response.body;
  }

  // fromDate defaults to the 2022/23 season start date
  async getRadioAudienceData({ fromDate } = { fromDate: '2022-10-04T00:00:00.000Z' }) {
    const [allEvents, audienceData] = await Promise.all([
      this.getCalendarEventsV3({ club: 1 }),
      callLambda(
        {
          operation: 'getRadioAudienceEntries',
        },
        BACKEND_LAMBDA
      ),
    ]);

    const audienceEntries = audienceData.body.map((item, index) => {
      return {
        ...item,
        event: allEvents.find(e => e.id === item.event_id),
        total: item.ca + item.us,
        index,
      };
    });
    log('[Audience]', 'Audience data payload', formatBytes(JSON.stringify(audienceData).length));
    return audienceEntries;
  }

  async getAudienceData({ fromDate, season } = {}) {
    const seasonFromDateMap = {
      '2022-23': '2022-10-07T00:00:00.000Z',
      '2023-24': '2023-10-10T00:00:00.000Z',
      '2024-25': '2024-10-04T00:00:00.000Z',
      '2025-26': '2025-10-01T00:00:00.000Z',
      '2026-27': '2026-09-29T00:00:00.000Z',
    };

    const effectiveFromDate = fromDate || seasonFromDateMap[season] || '2026-09-29T00:00:00.000Z';

    const [allEvents, outputsResponse, audienceData] = await Promise.all([
      this.getCalendarEventsV3({ club: 1 }),
      callLambda(
        {
          operation: 'getSpotRateData',
          data: { fromDate: effectiveFromDate },
        },
        BACKEND_LAMBDA
      ),
      callLambda(
        {
          operation: 'getDataOutputEntries',
          data: { fromDate: effectiveFromDate },
        },
        BACKEND_LAMBDA
      ),
    ]);

    const fieldMap = {
      o: 'output_id',
      ce: 'ca_est',
      ue: 'us_est',
      de: 'ded_est',
      se: 'sv_est',
      ge: 'gv_est',
      t60: 'tv_60',
      t15: 'tv_15',
      t10: 'tv_10',
      r60: 'radio_60',
      r30: 'radio_30',
      r15: 'radio_15',
      db: 'doublebox_15',
      d10: 'dropin_10',
      c10: 'cross_10',
      bb: 'billboard_10',
      e: 'event_id',
      n: 'network',
      t: 'team',
      s30: 'spot_30',
      cr: 'ca_rate',
      ur: 'us_rate',
      ae: 'a_est',
      aa: 'a_actual',
      c: 'cis',
      d: 'dach',
      nf: 'nordfb',
      no: 'nor',
      nv: 'nova',
      s: 'swe',
      v: 'via',
      i: 'intl_est',
    };

    function decodeAudienceItem(item) {
      const decoded = {};
      for (const [short, full] of Object.entries(fieldMap)) {
        decoded[full] = item[short];
      }
      // direct mapping (not in fieldMap, not shortened)
      decoded.id = item.id;
      decoded.ca = item.ca ?? 0;
      decoded.us = item.us ?? 0;
      decoded.ap = item.ap;
      decoded.fi = item.fi;
      return decoded;
    }

    const outputsMap = new Map(outputsResponse.body.map(o => [o.id, o]));
    const eventsMap = new Map(allEvents.map(e => [e.id, e]));

    const audienceEntries = audienceData.body.map((item, index) => {
      const decoded = decodeAudienceItem(item);
      const output = outputsMap.get(decoded.output_id);

      let spotRate;
      if (output?.stats) {
        spotRate = output.stats.split(',').map(v => parseFloat(v));
      }

      return {
        ...decoded,
        event: eventsMap.get(decoded.event_id),
        networks: decoded.network,
        total: (decoded.ca ?? 0) + (decoded.us ?? 0),
        total_est: (decoded.ca_est ?? 0) + (decoded.us_est ?? 0),
        index,
        ...(output && { complete: output.complete }),
        ...(spotRate && { spotRate }),
      };
    });

    log(
      'Audience',
      'Audience data payload for',
      `${season || effectiveFromDate}`,
      formatBytes(JSON.stringify(audienceData).length)
    );

    return audienceEntries;
  }

  async parseAllAudienceData(toast) {
    const parseCSV = csvData => {
      // split the CSV data into rows
      const rows = csvData
        .split('\n')
        .map(row => row.trim())
        .filter(Boolean);

      // Split the first row to get the headers
      const headers = rows[0].split(',').map(header => header.trim());

      // Helper function to map headers based on rules
      const mapHeader = header => {
        const lHeader = header.toLowerCase();
        const headerStringsToSkip = ['event', 'date', 'cpm', 'total audience', 'est. total'];

        if (headerStringsToSkip.includes(lHeader)) {
          return 'skip';
        }
        // CA ad rate
        else if (lHeader.includes('rate') && lHeader.includes('us')) {
          return 'us_rate';
        }
        // CA ad rate
        else if (lHeader.includes('rate') && lHeader.includes('ca')) {
          return 'ca_rate';
        }
        // US audience estimated
        else if (lHeader.includes('est') && lHeader.includes('us')) {
          return 'us_est';
        }
        // CA audience estimated
        else if (lHeader.includes('est') && lHeader.includes('ca')) {
          return 'ca_est';
        } else if (lHeader.includes('60 tv')) {
          return 'tv_60';
        } else if (lHeader.includes('15 tv')) {
          return 'tv_15';
        } else if (lHeader.includes('15 d') || lHeader.includes('box')) {
          return 'doublebox_15';
        } else if (lHeader.includes('10 tv')) {
          return 'tv_10';
        } else if (lHeader.includes('10 drop')) {
          return 'dropin_10';
        } else if (lHeader.includes('promo')) {
          return 'cross_10';
        } else if (lHeader.includes('board')) {
          return 'billboard_10';
        } else if (lHeader.includes('ancillary') && lHeader.includes('30')) {
          return 'ap';
        } else if (lHeader.includes('60 radio')) {
          return 'radio_60';
        } else if (lHeader.includes('30 radio')) {
          return 'radio_30';
        } else if (lHeader.includes('15 radio')) {
          return 'radio_15';
        }
        // :30 Spot aka 'manual' CPM
        else if (lHeader.includes('30') && lHeader.includes('spot')) {
          return 'spot_30';
        } else if (lHeader.includes('est') && lHeader.includes('ded')) {
          return 'ded_est';
        } else if (lHeader.includes('est') && lHeader.includes('sv')) {
          return 'sv_est';
        } else if (lHeader.includes('est') && lHeader.includes('gv')) {
          return 'gv_est';
        } else if (lHeader.includes('est') && lHeader.includes('ancillary')) {
          return 'a_est';
        } else if (lHeader.includes('actual') && lHeader.includes('ancillary')) {
          return 'a_actual';
        } else if (lHeader.includes('cisset')) {
          return 'cis';
        } else if (lHeader.includes('dach')) {
          return 'dach';
        } else if (lHeader.includes('fi') && lHeader.includes('tv')) {
          return 'fi';
        } else if (lHeader.includes('nord')) {
          return 'nordfb';
        } else if (lHeader.includes('nor') && lHeader.includes('tv')) {
          return 'nor';
        } else if (lHeader.includes('nova')) {
          return 'nova';
        } else if (lHeader.includes('swe') && lHeader.includes('tv')) {
          return 'swe';
        } else if (lHeader.includes('viaplay')) {
          return 'via';
        } else if (lHeader.includes('intl')) {
          return 'intl_est';
        } else if (lHeader.startsWith('network')) {
          return 'network';
        } else if (lHeader.includes('team')) {
          return 'team';
        } else if (lHeader.startsWith('game id')) {
          return 'id';
        } else {
          return null;
        }
      };

      let warning;
      const result = [];

      // Loop through the remaining rows
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i].split(',').map(cell => cell.trim());

        // create obj by mapping headers to corresponding row values
        const obj = {};

        for (let j = 0; j < headers.length; j++) {
          const mappedKey = mapHeader(headers[j]);

          if (mappedKey === 'skip') {
            continue;
          } else if (mappedKey) {
            obj[mappedKey] = row[j];
          } else {
            warning = `Header "${headers[j]}" cannot be recognized`;
            console.log(warning);
          }
        }

        result.push(obj);
      }

      // temporarily comment out toast for unknown headers since NHL has started using
      // the exported file to upload new data, which is causing false positive warnings
      // if (warning) {
      //   toast.current.show({
      //     severity: 'warn',
      //     summary: 'Unknown header',
      //     detail: warning,
      //     life: 7000,
      //   });
      // }

      return result;
    };

    const getContents = async filename => {
      const response = await this.readCSV(filename);
      const dataBuffer = Buffer.from(response.Body.data);
      const utf16Decoder = new TextDecoder('UTF-8');
      return utf16Decoder.decode(dataBuffer);
    };

    const normalizeNetworks = value => {
      return (value || '')
        .split(';')
        .map(item => item.trim())
        .filter(Boolean)
        .sort();
    };

    const sameNetworks = (a, b) => {
      if (a.length !== b.length) return false;
      return a.every((item, index) => item === b[index]);
    };

    const buildOutputsByEvent = rawOutputs => {
      const groupedByOutput = rawOutputs.reduce((acc, row) => {
        if (!acc[row.id]) {
          acc[row.id] = {
            id: row.id,
            event_id: row.event_id,
            codes: [],
          };
        }

        if (row.code) {
          acc[row.id].codes.push(row.code.trim());
        }

        return acc;
      }, {});

      const groupedOutputs = Object.values(groupedByOutput).map(output => ({
        ...output,
        codes: [...new Set(output.codes)].sort(),
      }));

      const outputsByEvent = groupedOutputs.reduce((acc, output) => {
        if (!acc[output.event_id]) {
          acc[output.event_id] = [];
        }

        acc[output.event_id].push(output);
        return acc;
      }, {});

      return outputsByEvent;
    };

    const findOutputMatch = (eventId, networkString, outputsByEvent) => {
      const eventOutputs = outputsByEvent[eventId] || [];
      const rowNetworks = normalizeNetworks(networkString);

      if (!rowNetworks.length || !eventOutputs.length) {
        return {
          outputId: undefined,
          matchType: 'not-found',
        };
      }

      // 1. Exact match by full set of networks
      const exactMatch = eventOutputs.find(output => sameNetworks(output.codes, rowNetworks));

      if (exactMatch) {
        return {
          outputId: exactMatch.id,
          matchType: 'exact-set',
        };
      }

      // 2. Fallback: try to resolve by individual networks
      // but only when the match is unambiguous inside the event
      const uniquelyMatchedOutputIds = new Set();

      for (const network of rowNetworks) {
        const outputsWithThisNetwork = eventOutputs.filter(output =>
          output.codes.includes(network)
        );

        if (outputsWithThisNetwork.length === 1) {
          uniquelyMatchedOutputIds.add(outputsWithThisNetwork[0].id);
        }
      }

      if (uniquelyMatchedOutputIds.size === 1) {
        return {
          outputId: [...uniquelyMatchedOutputIds][0],
          matchType: 'unique-network',
        };
      }

      if (uniquelyMatchedOutputIds.size > 1) {
        return {
          outputId: undefined,
          matchType: 'conflicting-unique-network',
        };
      }

      return {
        outputId: undefined,
        matchType: 'ambiguous-or-not-found',
      };
    };

    const [allEvents, outputsResponse] = await Promise.all([
      this.getCalendarEventsV3({ club: 1 }),
      callLambda(
        {
          operation: 'getAudienceOutputNetworkRows',
        },
        BACKEND_LAMBDA
      ),
    ]);

    const rawOutputs = outputsResponse.body || [];
    const outputsByEvent = buildOutputsByEvent(rawOutputs);

    const AUDIENCE_DATA_FILE = 'data.csv';
    const dataFile = AUDIENCE_DATA_PATH + AUDIENCE_DATA_FILE;
    const contents = await getContents(dataFile);

    const dataArray = parseCSV(contents)
      .map(row => {
        const event = allEvents.find(evt => evt.id == row.id); // must be '=='
        if (!event) return {};

        const match = findOutputMatch(event.id, row.network, outputsByEvent);

        // optional debug logs
        if (!match.outputId) {
          console.log('Output mapping failed', {
            eventId: event.id,
            gameId: row.id,
            csvNetwork: row.network,
            normalizedNetworks: normalizeNetworks(row.network),
            matchType: match.matchType,
            eventOutputs: outputsByEvent[event.id],
          });
        }

        return {
          ...row,
          event,
          outputId: match.outputId,
          networks: row.network, // TODO: use network property only. For now, it's being duplicated to be in sync with updateAudienceData()
          outputMatchType: match.matchType,
        };
      })
      .filter(row => row.event);

    return dataArray;
  }

  async parseRadioAudienceData() {
    // helpers
    const parseCSV = csvData => {
      // split the CSV data into rows
      const rows = csvData
        .split('\n')
        .map(row => row.trim())
        .filter(Boolean);

      // Split the first row to get the headers
      const headers = rows[0].split(',').map(header => header.trim());

      // Helper function to map headers based on rules
      const mapHeader = header => {
        const lHeader = header.toLowerCase();
        const headerStringsToSkip = ['event', 'date', 'cpm', 'total audience', 'est. total'];
        if (headerStringsToSkip.includes(lHeader)) {
          return 'skip';
        }
        // CA ad rate
        else if (lHeader.includes('rate') && lHeader.includes('us')) {
          return 'us_rate';
        }
        // CA ad rate
        else if (lHeader.includes('rate') && lHeader.includes('ca')) {
          return 'ca_rate';
        }
        // US audience estimated
        else if (lHeader.includes('est') && lHeader.includes('us')) {
          return 'us_est';
        }
        // CA audience estimated
        else if (lHeader.includes('est') && lHeader.includes('ca')) {
          return 'ca_est';
        } else if (lHeader.includes('60 tv')) {
          return 'tv_60';
        } else if (lHeader.includes('15 tv')) {
          return 'tv_15';
        } else if (lHeader.includes('15 d') || lHeader.includes('box')) {
          return 'doublebox_15';
        } else if (lHeader.includes('10 tv')) {
          return 'tv_10';
        } else if (lHeader.includes('10 drop')) {
          return 'dropin_10';
        } else if (lHeader.includes('promo')) {
          return 'cross_10';
        } else if (lHeader.includes('board')) {
          return 'billboard_10';
        } else if (lHeader.includes('ancillary')) {
          return 'ap';
        } else if (lHeader.includes('60 radio')) {
          return 'radio_60';
        } else if (lHeader.includes('30 radio')) {
          return 'radio_30';
        } else if (lHeader.includes('15 radio')) {
          return 'radio_15';
          // :30 Spot aka 'manual' CPM
        } else if (lHeader.includes('30') && lHeader.includes('spot')) {
          return 'spot_30';
        } else if (lHeader.includes('est') && lHeader.includes('ded')) {
          return 'ded_est';
        } else if (lHeader.includes('est') && lHeader.includes('sv')) {
          return 'sv_est';
        } else if (lHeader.includes('est') && lHeader.includes('gv')) {
          return 'gv_est';
        } else if (lHeader.startsWith('network')) {
          return 'network';
        } else if (lHeader.includes('team')) {
          return 'team';
        } else if (lHeader.startsWith('game id')) {
          return 'id';
          // radio :30 ad rate
        } else if (lHeader.includes('30') && lHeader.includes('rate')) {
          return 'ad_rate';
        } else if (lHeader.includes('ca') && lHeader.includes('audience')) {
          return 'ca';
        } else if (lHeader.includes('us') && lHeader.includes('audience')) {
          return 'us';
        } else {
          return null;
        }
      };

      let warning;
      const result = [];

      // Loop through the remaining rows
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i].split(',').map(cell => cell.trim());

        // create obj by mapping headers to corresponding row values
        const obj = {};

        for (let j = 0; j < headers.length; j++) {
          const mappedKey = mapHeader(headers[j]);
          if (mappedKey === 'skip') {
            continue;
          } else if (mappedKey) {
            obj[mappedKey] = row[j];
          } else {
            warning = `Header "${headers[j]}" cannot be recognized`;
            console.log(warning);
          }
        }

        result.push(obj);
      }

      return result;
    };

    const getContents = async filename => {
      const response = await this.readCSV(filename);
      const dataBuffer = Buffer.from(response.Body.data);
      const utf16Decoder = new TextDecoder('UTF-8');
      return utf16Decoder.decode(dataBuffer);
    };

    // main
    const [allEvents, outputsResponse] = await Promise.all([
      this.getCalendarEventsV3({ club: 1 }),
      callLambda(
        {
          operation: 'getRadioAudienceOutputMappings',
        },
        BACKEND_LAMBDA
      ),
    ]);
    const outputs = outputsResponse.body;

    const AUDIENCE_DATA_FILE = 'radio.csv';
    const dataFile = AUDIENCE_DATA_PATH + AUDIENCE_DATA_FILE;
    const contents = await getContents(dataFile, allEvents);

    const dataArray = parseCSV(contents)
      .map(row => {
        const event = allEvents.find(evt => evt.id == row.id); // must be '=='
        if (!event) return {};

        const output = outputs.find(o => o.event_id == event.id && o.network == row.network);
        const outputId = output ? output.id : undefined;
        return {
          ...row,
          event,
          outputId,
          networks: row.network, // TODO: use network property only. For now, it's being duplicated to be in sync with updateAudienceData()
        };
      })
      .filter(row => row.event);
    return dataArray;
  }

  // handles CA/US audience file upload
  // TODO: combine with the same functions for the ALL uploader
  async parseAudienceFileAndUpdateDB(filename) {
    const getContents = async (filename, allEvents) => {
      const response = await this.readCSV(filename);
      const dataBuffer = Buffer.from(response.Body.data);
      const utf16Decoder = new TextDecoder('UTF-8');
      const contents = utf16Decoder.decode(dataBuffer);

      let arrContents = contents.split(/\r?\n/g);
      // A workaround to identify the order of columns
      // (to handle the fact that NHL uses different order
      // of columns in 2022/23 and 2023/24 season files:
      // i.e. it's ID, Team, Networks, Audience in 2022/23
      // and       ID, Networks, Team, Audience in 2023/24)
      let secondColumn =
        arrContents[0] && arrContents[0].split(',').length > 1 && arrContents[0].split(',')[1];
      if (secondColumn.toLowerCase().includes('network')) {
        secondColumn = 'network';
      }

      return arrContents
        .map(row => {
          // replace commas with semicolons inside quotes
          const row_ = row.replace(/"[^"]+"/g, function (v) {
            return v.replace(/,/g, ';');
          });

          let [id, team, networks, value] = row_.split(',');
          if (secondColumn === 'network') {
            [id, networks, team, value] = row_.split(',');
          }
          return {
            id,
            team,
            networks,
            value,
          };
        })
        .map(row => ({
          event: allEvents.find(evt => evt.id == row.id), // must be '=='
          value: row.value ? parseInt(row.value) : 0,
          team: row.team,
          networks: row.networks,
          id: row.id,
        }))
        .filter(row => row.event);
    };
    const [allEvents, outputsResponse] = await Promise.all([
      this.getCalendarEventsV3({ club: 1 }),
      callLambda(
        {
          operation: 'getAudienceOutputNetworkRows',
        },
        BACKEND_LAMBDA
      ),
    ]);

    const outputs = outputsResponse.body; // outputs with spot rate and networks info
    const filePath = AUDIENCE_DATA_PATH + filename;

    const contents = await getContents(filePath, allEvents);
    const fieldToChange = filename === AUDIENCE_FILE_CA ? 'ca' : 'us';

    const rows = contents.map(output => {
      const output_ = outputs.find(o => o.event_id == output.id && o.code === output.networks);

      return {
        event_id: output.id,
        network: output.networks,
        output_id: output_ ? output_.id : null,
        team: output.team,
        value: output.value,
      };
    });

    const response = await callLambda(
      {
        operation: 'upsertRegionalAudienceData',
        data: {
          field: fieldToChange,
          rows,
        },
      },
      BACKEND_LAMBDA
    );
    console.log(response);
  }

  // Update output/audience data based on CSV files
  // uploaded by the NHL admin
  // TODO: remove
  async parseAudienceData() {
    const combineContents = (arr1, arr2) => {
      const arr3 = [];
      for (const item1 of arr1) {
        let found = false;
        for (const item2 of arr2) {
          if (
            item1.id === item2.id &&
            item1.networks === item2.networks &&
            item1.team === item2.team
          ) {
            arr3.push({
              id: item1.id,
              networks: item1.networks,
              team: item1.team,
              value1: item1.value,
              value2: item2.value,
              total: item1.value + item2.value,
            });
            found = true;
            break;
          }
        }
        if (!found) {
          arr3.push({
            id: item1.id,
            networks: item1.networks,
            team: item1.team,
            value1: item1.value,
            value2: 0,
            total: item1.value,
          });
        }
      }

      for (const item2 of arr2) {
        let found = false;
        for (const item3 of arr3) {
          if (item2.id === item3.id && item2.networks === item3.networks) {
            found = true;
            break;
          }
        }
        if (!found) {
          arr3.push({
            id: item2.id,
            networks: item2.networks,
            team: item2.team,
            value1: 0,
            value2: item2.value,
            total: item2.value,
          });
        }
      }
      return arr3;
    };
    // read CSV file and return the array of objects
    // { id, teams, networks, value }
    const getContents = async (filename, allEvents) => {
      const response = await this.readCSV(filename);
      const dataBuffer = Buffer.from(response.Body.data);
      const utf16Decoder = new TextDecoder('UTF-8');
      const contents = utf16Decoder.decode(dataBuffer);

      let arrContents = contents.split(/\r?\n/g);
      // A workaround to identify the order of columns
      // (to handle the fact that NHL uses different order
      // of columns in 2022/23 and 2023/24 season files:
      // i.e. it's ID, Team, Networks, Audience in 2022/23
      // and       ID, Networks, Team, Audience in 2023/24)
      let secondColumn =
        arrContents[0] && arrContents[0].split(',').length > 1 && arrContents[0].split(',')[1];
      if (secondColumn.toLowerCase().includes('network')) {
        secondColumn = 'network';
      }

      return arrContents
        .map(row => {
          // replace commas with semicolons inside quotes
          const row_ = row.replace(/"[^"]+"/g, function (v) {
            return v.replace(/,/g, ';');
          });

          let [id, team, networks, value] = row_.split(',');
          if (secondColumn === 'network') {
            [id, networks, team, value] = row_.split(',');
          }
          return {
            id,
            team,
            networks,
            value,
          };
        })
        .map(row => ({
          event: allEvents.find(evt => evt.id == row.id), // must be '=='
          value: row.value ? parseInt(row.value) : 0,
          team: row.team,
          networks: row.networks,
          id: row.id,
        }))
        .filter(row => row.event);
    };

    const [allEvents, outputsResponse] = await Promise.all([
      this.getCalendarEventsV3({ club: 1 }),
      callLambda(
        {
          operation: 'getSpotRateData',
        },
        BACKEND_LAMBDA
      ),
    ]);
    const outputs = outputsResponse.body; // outputs with spot rate and networks info

    const caFile = AUDIENCE_DATA_PATH + AUDIENCE_FILE_CA;
    const usFile = AUDIENCE_DATA_PATH + AUDIENCE_FILE_US;
    const [caContents, usContents] = await Promise.all([
      getContents(caFile, allEvents),
      getContents(usFile, allEvents),
    ]);

    let combined = combineContents([...caContents], [...usContents]);
    combined = combined.map(row => {
      // '==' used intentionally
      const event = allEvents.find(evt => evt.id == row.id);
      return {
        event,
        date: event ? new Date(event.et_time) : '',
        total: row.total,
        ca: row.value1,
        us: row.value2,
        networks: row.networks,
        team: row.team,
        id: row.id,
      };
    });

    // transform to dictionary, groupped by output's event_id
    const groupped = this.groupBy(combined, 'id');
    let index = 1;
    for (const id in groupped) {
      const eventOutputs = outputs.filter(o => id == o.event_id);
      for (let i = 0; i < groupped[id].length; i++) {
        const output = eventOutputs.find(o =>
          o.networks.split(',').includes(groupped[id][i].networks)
        );
        let networks = output ? output.networks.split(',') : [];
        let spotRate;
        // set audience data completion status
        groupped[id][i].complete = output ? output.complete : null;
        // unique index to be used in Audience Data table
        groupped[id][i].index = index++;
        if (output) {
          groupped[id][i].outputId = output.id;
        }

        if (output && output.stats) {
          spotRate = output.stats.split(',').map(v => parseFloat(v));
          if (networks.length === 1) {
            groupped[id][i].spotRate = spotRate;
          } else if (networks.length > 1) {
            const thisFeedEntries = groupped[id].filter(entry => networks.includes(entry.networks));
            const elementWithMaxTotal = thisFeedEntries.reduce((maxElement, obj) => {
              if (obj.total > maxElement.total) return obj;
              else return maxElement;
            });
            groupped[id] = groupped[id].map(el => {
              if (el.networks === elementWithMaxTotal.networks) {
                return { ...el, spotRate };
              } else return el;
            });
          }
        }
      }
    }

    // transform dictionary back to array
    const array = Object.values(groupped)
      .flat()
      .map(item => {
        if (item.event.id === 2022020614 && item.team === 'CAR') return { ...item, outputId: 6931 };
        return item;
      });

    return array;
  }

  async updateAudienceData(data) {
    if (!data.length) return;
    const outputList = data.map(item => {
      return {
        ca_rate: item.ca_rate,
        us_rate: item.us_rate,
        // ca: item.ca,
        // us: item.us,
        ca_est: item.ca_est,
        us_est: item.us_est,
        ded_est: item.ded_est,
        sv_est: item.sv_est,
        gv_est: item.gv_est,
        tv_60: item.tv_60,
        tv_15: item.tv_15,
        tv_10: item.tv_10,
        ap: item.ap,
        radio_60: item.radio_60,
        radio_30: item.radio_30,
        radio_15: item.radio_15,
        spot_30: item.spot_30,
        doublebox_15: item.doublebox_15,
        dropin_10: item.dropin_10,
        cross_10: item.cross_10,
        billboard_10: item.billboard_10,
        a_est: item.a_est,
        a_actual: item.a_actual,
        cis: item.cis,
        dach: item.dach,
        fi: item.fi,
        nordfb: item.nordfb,
        nor: item.nor,
        nova: item.nova,
        swe: item.swe,
        via: item.via,
        intl_est: item.intl_est,
        event_id: item.event.id,
        output_id: item.outputId, // can be undefined
        network: item.networks,
        team: item.team,
      };
    });

    const response = await callLambda(
      {
        operation: 'upsertAudienceData',
        data: { rows: outputList },
      },
      BACKEND_LAMBDA
    );

    console.log(response);
  }

  // update radio audience data
  async updateRadioAudienceData(data) {
    const convertNAs = value => {
      if (value && (value.includes('NA') || value.includes('N/A'))) {
        return 0;
      }
      return value;
    };
    if (!data.length) return;
    const outputList = data.map(item => {
      return {
        event_id: item.event.id,
        output_id: item.outputId, // can be undefined
        network: item.networks,
        team: item.team,
        ca: convertNAs(item.ca),
        us: convertNAs(item.us),
        ad_rate: convertNAs(item.ad_rate),
      };
    });

    const response = await callLambda(
      {
        operation: 'upsertRadioAudienceData',
        data: { rows: outputList },
      },
      BACKEND_LAMBDA
    );

    console.log(response);
  }

  // scan raw stats json
  async scanMetrics(jsonPath) {
    const response = await callLambda(
      {
        operation: 'scanJson',
        data: { path: jsonPath },
      },
      STATS_LAMBDA
    );
    return response;
  }
  // read raw stats json
  async readMetrics(jsonPath) {
    const response = await callLambda(
      {
        operation: 'readMetrics',
        data: { path: jsonPath },
      },
      STATS_LAMBDA
    );
    return response;
  }
  // read a CSV file
  async readCSV(path) {
    const response = await callLambda(
      {
        operation: 'readCSV',
        data: { path: path },
      },
      STATS_LAMBDA
    );
    return response;
  }
  // update raw stats json
  async updateMetrics(jsonPath, jsonContents) {
    const response = await callLambda(
      {
        operation: 'updateMetrics',
        data: { path: jsonPath, json: jsonContents },
      },
      STATS_LAMBDA
    );
    return response;
  }
  // delete stats_output object by output_id
  async deleteMetrics(output_id) {
    const response = await callLambda(
      {
        operation: 'deleteStats',
        data: { output_id },
      },
      BACKEND_LAMBDA
    );
    return response;
  }
  // precalculate stats to use in the reporting page
  async precalculateFeed(feed, properId, sendToMVP) {
    const response = await callLambda(
      {
        operation: 'precalculateFeed',
        data: { ...feed, properId, sendToMVP: sendToMVP },
      },
      STATS_LAMBDA_NODE24
    );
    return response;
  }
  // copy a json stats file to serve as
  // a stats source for the secondary feed
  async copyJson(data) {
    console.log(data);
    const response = await callLambda(
      {
        operation: 'copyJson',
        data: data,
      },
      STATS_LAMBDA
    );
    return response;
  }

  // copy stats_ouptut obj for a given output
  async copyStatsOutput(data) {
    const response = await callLambda(
      {
        operation: 'copyStatsOutput',
        data: data,
      },
      BACKEND_LAMBDA
    );
    return response.body;
  }

  async createAllocations(output_id, ad_ids) {
    console.log(output_id, ad_ids);
    const result = await callLambda({
      hub2: true,
      create: true,
      operation: 'createAllocations',
      output_id: output_id,
      ad_ids: ad_ids,
    });
    console.log(result);
  }
  async deleteAllocation(output_id, advert_id) {
    // TODO: use ad_ids array instead of advert_id to batch delete multiple allocations
    console.log(output_id, advert_id);
    //const result = await callLambda({ 'hub2': true, 'delete': true, 'operation': 'deleteAllocations', 'output_id': output_id, 'advert_id': advert_id});
    const response = await callLambda(
      {
        operation: 'deleteAllocation',
        data: { output_id, advert_id },
      },
      'hub2-api-venue-post'
    );
    console.log(response);
    return response;
  }

  async createEvent(event) {
    let imageURL = '';
    if (event.image) {
      const now = new Date();
      const fileKey = appendToFilename(S3_PREFIX + event.image.name, '_' + now.getTime());
      await this.uploadImage(event.image, fileKey);
      imageURL = buildS3Url(MEDIA_BUCKET, fileKey);
    }
    const segments = event.segments.map(segment => {
      return {
        sequence: segment.sequence,
        name: segment.name,
        duration: segment.duration,
      };
    });
    const triggers = event.triggers.map(segment => {
      return {
        sequence: segment.sequence,
        name: segment.name,
      };
    });
    const input = {
      // id: event.id,
      name: event.name,
      date_time: event.date_time,
      sportId: event.sport.id,
      venueId: event.venue.id,
      // competitionId: event.competitionId ? event.competitionId : null,
      // competition: event.competitionId, // TODO: remove this from DB schema
      visible: event.visible,
      segments,
      triggers,
      zones: event.zones,
      // conditionally add optional properties
      ...(event.image && { image: imageURL }),
      ...(event.competitionId && { competitionId: event.competitionId }),
      // ...event.competitionId && { competition: event.competitionId },
      ...(event.duration && { duration: event.duration }),
      ...(event.ad_threshold && { ad_threshold: event.ad_threshold }),
    };

    const result = await callLambda({ operation: 'createEvent', data: input });

    return { id: result.body.id, imageURL };
    // return { ...result.data.createEvent };
  }
  // rename output or update output 'complete' status
  async updateOutput(output) {
    const response = await callLambda(
      {
        operation: 'updateOutput',
        data: output,
      },
      BACKEND_LAMBDA
    );
    return response.body;
  }
  async updateEvent(event, oldEvent) {
    // there are 3 options here for event.image
    // if neither oldEvent nor event has image attached
    //    => event.image is null or ''
    // if oldEvent had image attached and image wasn't changed on update
    //    => event.image is string here (url)
    // else a new image was uploaded on update (regardles of oldEvent image existance)
    //    => event.image is a file object here {name: ..., size: ..., etc.}
    let imageUpdated = false;
    let imageURL = null;
    // console.log(event.image)
    // image uploaded
    if (event.image && event.image.name) {
      imageUpdated = true;
      const now = new Date();
      const fileKey = appendToFilename(S3_PREFIX + event.image.name, '_' + now.getTime());
      // upload image
      await this.uploadImage(event.image, fileKey);
      imageURL = buildS3Url(MEDIA_BUCKET, fileKey);
    }
    // image removed
    else if (!event.image && oldEvent.image) {
      imageUpdated = true;
      imageURL = '';
    } else {
      // return imageURL in the end,
      // so need to assign an existing image url to it
      imageURL = event.image;
    }
    const segments = event.sport.segments.map(segment => {
      return {
        sequence: segment.sequence,
        name: segment.name,
        event_id: event.id,
        duration: segment.duration,
      };
    });
    const triggers = event.sport.triggers.map(segment => {
      return {
        sequence: segment.sequence,
        name: segment.name,
        event_id: event.id,
      };
    });

    const input = {
      id: event.id,
      name: event.name,
      date_time: event.date_time,
      sportId: event.sport.id,
      venueId: event.venue.id,
      visible: event.visible,
      segments,
      triggers,
      zones: event.zones,
      // conditionally add optional properties
      ...(event.competitionId && { competitionId: event.competitionId }),
      ...(imageUpdated && { image: imageURL }),
      ...(event.duration && { duration: event.duration }),
      ...(event.ad_threshold && { ad_threshold: event.ad_threshold }),
    };

    await callLambda({ hub2: true, update: true, operation: 'updateEvent', data: input });

    return { imageURL };
  }

  async getPlaylistValidationSettings() {
    const response = await callLambda(
      { operation: 'getPlaylistValidationSettings' },
      BACKEND_LAMBDA
    );
    return response.body;
  }

  async updatePlaylistValidationSettings({ audiences, rules, user }) {
    const response = await callLambda(
      {
        operation: 'updatePlaylistValidationSettings',
        data: { audiences, rules, user },
      },
      BACKEND_LAMBDA
    );
    return response.body;
  }

  async savePlaylist(imps, output_id, type, user) {
    const placements = [];
    const impressionIds = [];

    let zoneIndexOffset = 1;

    // TODO: make a global var
    const ZONE_INDEXES = {
      DED: [1, 2, 3, 4, 5],
      SV: [6, 7],
      GV: [8, 9, 10, 11],
      TV: [12],
      Radio: [13],
      II: [14, 15, 16, 17, 18, 19, 20, 21],
      CCZ: [CENTER_CIRCLE_ZONE_SEQUENCE],
    };
    const zoneIndexes = ZONE_INDEXES[type];
    const dedZoneNumber = ZONE_INDEXES['DED'].length;
    const svZoneNumber = ZONE_INDEXES['SV'].length;
    const gvZoneNumber = ZONE_INDEXES['GV'].length;
    const tvZoneNumber = 1;
    const radioZoneNumber = 1;
    const iiZoneNumber = 8;

    if (type === 'SV') {
      zoneIndexOffset += dedZoneNumber;
    } else if (type === 'GV') {
      zoneIndexOffset += dedZoneNumber + svZoneNumber;
    } else if (type === 'TV') {
      zoneIndexOffset += dedZoneNumber + svZoneNumber + gvZoneNumber;
    } else if (type === 'Radio') {
      zoneIndexOffset += dedZoneNumber + svZoneNumber + gvZoneNumber + tvZoneNumber;
    } else if (type === 'II') {
      zoneIndexOffset +=
        dedZoneNumber + svZoneNumber + gvZoneNumber + tvZoneNumber + radioZoneNumber;
    } else if (type === 'CCZ') {
      zoneIndexOffset = CENTER_CIRCLE_ZONE_SEQUENCE;
    }

    imps.forEach(impression => {
      // skip empty (header) rows
      if (impression.id) {
        impressionIds.push(impression.id);

        impression.placements.map((cellPlacements, placementIndex) => {
          if (cellPlacements[0].id) {
            placements.push({
              advert_id: cellPlacements[0].id,
              zone_sequence: placementIndex + zoneIndexOffset,
              impression_id: impression.id,
              sequence: 1,
            });
          }
          if (cellPlacements[1].id) {
            placements.push({
              advert_id: cellPlacements[1].id,
              zone_sequence: placementIndex + zoneIndexOffset,
              impression_id: impression.id,
              sequence: 2,
            });
          }
        });
      }
    });
    const response = await callLambda(
      {
        operation: 'savePlaylist_NEW',
        data: { output_id, placements, impressionIds, user, zoneIndexes },
      },
      BACKEND_LAMBDA
    );
    console.log('playlist saved:', response);
  }

  async inspectDedPlacementAssets(imps = [], outputId = null) {
    const placements = [];
    for (const imp of imps) {
      if (!imp?.id) continue;
      (imp.placements || []).forEach((cell, zoneIndex) => {
        cell.forEach((ad, subzoneIndex) => {
          if (ad?.id) placements.push({
            impression_id: imp.id,
            advert_id: ad.id,
            zone_sequence: zoneIndex + 1,
            sequence: subzoneIndex + 1,
          });
        });
      });
    }
    const response = await callLambda({
      operation: 'inspectDedPlacementAssets',
      data: { placements, outputId },
    }, BACKEND_LAMBDA);
    return response.body?.issues || [];
  }

  async saveMultiplePlaylists(
    imps,
    sourceOutputId,
    type,
    user,
    outputIds,
    nonEmptyCellCheck,
    zoneIndexesOverride = null,
    options = {}
  ) {
    const placements = [];
    const impressionIds = [];

    let zoneIndexOffset = 1;

    // TODO: make a global var
    const ZONE_INDEXES = {
      DED: [1, 2, 3, 4, 5],
      SV: [6, 7],
      GV: [8, 9, 10, 11],
      TV: [12],
      Radio: [13],
      II: [14, 15, 16, 17, 18, 19, 20, 21],
      CCZ: [CENTER_CIRCLE_ZONE_SEQUENCE],
    };

    const defaultZoneIndexes = ZONE_INDEXES[type];
    const effectiveZoneIndexes = zoneIndexesOverride?.length
      ? zoneIndexesOverride
      : defaultZoneIndexes;

    const dedZoneNumber = ZONE_INDEXES['DED'].length;
    const svZoneNumber = ZONE_INDEXES['SV'].length;
    const gvZoneNumber = ZONE_INDEXES['GV'].length;
    const tvZoneNumber = 1;
    const radioZoneNumber = 1;

    if (type === 'SV') {
      zoneIndexOffset += dedZoneNumber;
    } else if (type === 'GV') {
      zoneIndexOffset += dedZoneNumber + svZoneNumber;
    } else if (type === 'TV') {
      zoneIndexOffset += dedZoneNumber + svZoneNumber + gvZoneNumber;
    } else if (type === 'Radio') {
      zoneIndexOffset += dedZoneNumber + svZoneNumber + gvZoneNumber + tvZoneNumber;
    } else if (type === 'II') {
      zoneIndexOffset +=
        dedZoneNumber + svZoneNumber + gvZoneNumber + tvZoneNumber + radioZoneNumber;
    } else if (type === 'CCZ') {
      zoneIndexOffset = CENTER_CIRCLE_ZONE_SEQUENCE;
    }

    imps.forEach(impression => {
      // skip empty (header) rows
      if (impression.id) {
        impressionIds.push(impression.id);

        impression.placements.forEach((cellPlacements, placementIndex) => {
          const zoneSequence = placementIndex + zoneIndexOffset;

          // save only the zones that are actually allowed for this operation
          if (!effectiveZoneIndexes.includes(zoneSequence)) {
            return;
          }

          if (cellPlacements?.[0]?.id) {
            placements.push({
              advert_id: cellPlacements[0].id,
              zone_sequence: zoneSequence,
              impression_id: impression.id,
              sequence: 1,
            });
          }

          if (cellPlacements?.[1]?.id) {
            placements.push({
              advert_id: cellPlacements[1].id,
              zone_sequence: zoneSequence,
              impression_id: impression.id,
              sequence: 2,
            });
          }
        });
      }
    });

    const normalizedOutputIds = [
      ...new Set((outputIds || []).map(Number).filter(Number.isInteger)),
    ];

    const lambdaOperation =
      type === 'TV' || type === 'Radio' ? 'saveMultiplePlaylists' : 'saveMultiplePlaylistsV2';

    const response = await callLambda(
      {
        operation: lambdaOperation,
        data: {
          sourceOutputId: Number(sourceOutputId),
          placements,
          impressionIds,
          user,
          zoneIndexes: effectiveZoneIndexes,
          outputIds: normalizedOutputIds,
          nonEmptyCellCheck,
          mirrorCenterCircleAwayFeeds: Boolean(
            options.mirrorCenterCircleAwayFeeds
          ),
          mirrorInIceFeeds: Boolean(options.mirrorInIceFeeds),
          saveSourceOutput: Boolean(options.saveSourceOutput),
        },
      },
      BACKEND_LAMBDA
    );

    console.log(normalizedOutputIds.length, 'playlists saved:', response);
    return response;
  }

  async getClubPlaylistWizardTargets({ sourceOutputId, playlistType, user }) {
    const response = await callLambda(
      {
        operation: 'getClubPlaylistWizardTargets',
        data: {
          sourceOutputId: Number(sourceOutputId),
          playlistType,
          user,
        },
      },
      BACKEND_LAMBDA
    );

    return response.body;
  }

  async saveClubPlaylistWizard(
    imps,
    sourceOutputId,
    playlistType,
    user,
    outputIds,
    nonEmptyCellCheck,
    options = {}
  ) {
    const zoneIndexesByType = {
      DED: [1, 2, 3, 4, 5],
      SV: [6, 7],
      GV: [8, 9, 10, 11],
      II: [14, 15, 16, 17, 18, 19, 20, 21],
      CCZ: [CENTER_CIRCLE_ZONE_SEQUENCE],
    };
    const zoneIndexes = zoneIndexesByType[playlistType];
    if (!zoneIndexes) {
      throw new Error(`Unsupported Club Playlist Wizard type: ${playlistType}`);
    }

    const zoneIndexOffset = zoneIndexes[0];
    const placements = [];
    const impressionIds = [];

    imps.forEach(impression => {
      if (!impression.id) return;
      impressionIds.push(impression.id);

      impression.placements.forEach((cellPlacements, placementIndex) => {
        const zoneSequence = placementIndex + zoneIndexOffset;
        if (!zoneIndexes.includes(zoneSequence)) return;

        if (cellPlacements?.[0]?.id) {
          placements.push({
            advert_id: cellPlacements[0].id,
            zone_sequence: zoneSequence,
            impression_id: impression.id,
            sequence: 1,
          });
        }
        if (cellPlacements?.[1]?.id) {
          placements.push({
            advert_id: cellPlacements[1].id,
            zone_sequence: zoneSequence,
            impression_id: impression.id,
            sequence: 2,
          });
        }
      });
    });

    return callLambda(
      {
        operation: 'saveClubPlaylistWizard',
        data: {
          sourceOutputId: Number(sourceOutputId),
          playlistType,
          user,
          outputIds,
          impressionIds,
          placements,
          nonEmptyCellCheck,
          skipInIceUsNationalMirror: Boolean(
            options.skipInIceUsNationalMirror
          ),
          saveSourceOutput: Boolean(options.saveSourceOutput),
        },
      },
      BACKEND_LAMBDA
    );
  }

  async saveExportToDb(event_id) {
    const lambdaResponse = await callLambda(
      {
        hub2: true,
        exportType: 'eventExport',
        id: event_id,
        name: 'saveExportToDb',
      },
      EXPORT_LAMBDA
    );
    console.log(lambdaResponse);
  }

  async createOutput(output, playoffs = false) {
    const defaultDuration = 30;

    const input = {
      name: output.name,
      event_id: output.event_id,
      set_duration: defaultDuration,
    };
    const operation = playoffs ? 'createOutput_playoffs' : 'createOutput';
    const response = await callLambda(
      { hub2: true, operation: operation, data: input },
      BACKEND_LAMBDA
    );
    console.log('create output:', response);
    return response.body;
  }
  // TODO: merge this into createOutput() function
  // use an extra arg to specify whether it's template or not
  async createOutputFromPlaylistManager(output, playoffs = false) {
    const defaultDuration = 30;
    const input = {
      name: output.name,
      event_id: output.event_id,
      set_duration: defaultDuration,
    };
    const operation = playoffs ? 'createOutput_playoffs' : 'createOutputFromPlaylistManager';
    const response = await callLambda({ operation: operation, data: input }, BACKEND_LAMBDA);
    console.log('create output:', response);
    return response.body;
  }

  // deleting templates in Playlist page
  async deleteOutput(id) {
    const input = { id };
    const response = await callLambda(
      {
        operation: 'deleteOutput',
        data: input,
      },
      BACKEND_LAMBDA
    );
    console.log('response:', response);
    return response.body;
  }

  async exportOutput(event, output, adverts, advertisers, impressions, placements) {
    const result = await callLambda({
      operation: 'exportOutput',
      event: event,
      output: output,
      adverts: adverts,
      advertisers: advertisers,
      impressions: impressions,
      placements: placements,
    });
    console.log(result);
  }

  async uploadImage(image, fileKey) {
    const params = {
      Bucket: MEDIA_BUCKET,
      Key: fileKey,
      ContentType: image.type,
      Body: image,
    };

    const authService = new AuthService();
    const s3 = await authService.getS3();
    await s3
      .upload(params, function (err, data) {
        if (err) {
          console.log('An error occurred while completing the upload', err);
        } else {
          console.log('Final upload data:', data);
        }
      })
      .promise();
  }
  async deleteEvent(event) {
    const result = await callLambda({ operation: 'deleteEvent', id: event.id });
    return result;
  }
  async getObject(key, bucket, toast) {
    try {
      const options = {
        Bucket: bucket,
        Key: key,
        ResponseContentType: 'application/json',
        ResponseCacheControl: 'No-cache', // ensure json file is not cached
      };
      const authService = new AuthService();
      const s3 = await authService.getS3();
      const data = await s3.getObject(options).promise();
      const arr = data.Body;
      let str = '';
      for (let i = 0; i < arr.byteLength; i++) {
        str += String.fromCharCode(arr[i]);
      }
      return JSON.parse(str);
    } catch (e) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: `Can't read JSON stats file. Error message: ${e.message}`,
        life: 7000,
      });
      return null;
    }
  }

  handleCountryCodes = venues => {
    if (!venues) return [];
    const r = venues.map(venue => ({
      ...venue,
      country: countryList.find(obj => {
        return obj.name === venue.region;
      }),
    }));
    return r;
  };
  sortObjectsByName(arr) {
    arr.sort(function (a, b) {
      const textA = a.name.toUpperCase();
      const textB = b.name.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
    return arr;
  }
}
